<template>
  <!-- 입고오류 리스트(入库错误列表) — YZY 요구로 이 화면의 모든 표시는 중국어만(한국어 병기 없음, 2026-10-03).
       다른 쿠패스 화면은 기존처럼 병기한다 -->
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
            <b-button :variant="mentionList.length ? 'danger' : 'outline-secondary'" size="sm" class="font-weight-bold" title="被@的待确认消息" @click="showMentions = true">
              @待确认 <b-badge :variant="mentionList.length ? 'light' : 'secondary'">{{ mentionList.length }}</b-badge>
            </b-button>
          </div>
          <div class="card-body">
            <!-- 필터 -->
            <div class="d-flex flex-wrap align-items-center mb-3 daiban-filterbar" style="gap:8px">
              <label class="mb-0 small">登记日期</label>
              <input type="date" v-model="startdate" class="form-control form-control-sm" style="width:150px">
              <span>~</span>
              <input type="date" v-model="enddate" class="form-control form-control-sm" style="width:150px">
              <label class="mb-0 small ml-2">货主</label>
              <input type="text" v-model.trim="companyid" list="importErrorCompanyList" placeholder="全部"
                     class="form-control form-control-sm" style="width:160px" @keyup.enter="search">
              <datalist id="importErrorCompanyList">
                <option v-for="c in companyOptions" :key="c" :value="c"></option>
              </datalist>
              <label class="mb-0 small ml-2">订单号/货主</label>
              <input type="text" v-model.trim="keyword" placeholder="订单号·货主订单号·货主" title="搜索时也包含已完成的"
                     class="form-control form-control-sm" style="width:170px">
              <label class="mb-0 small ml-2" title="登记错误的员工(列表「创建人」列)">创建人</label>
              <b-form-select v-model="creatorFilter" :options="creatorOptions" size="sm" style="width:150px"></b-form-select>
              <label class="mb-0 small ml-2" title="代办明细의 «完成代办» 버튼을 누른 건만 已完成, 나머지는 未完成. «<已删除>» = 삭제한 건(보기만 가능)">代办状态</label>
              <b-form-select v-model="statusFilter" :options="statusFilterOptions" size="sm" style="width:140px"></b-form-select>
              <b-button size="sm" variant="primary" @click="search" :disabled="loading">搜索</b-button>
              <b-button size="sm" variant="success" @click="downloadExcel" :disabled="!viewRows.length || exporting">
                <b-spinner v-if="exporting" small></b-spinner> ⬇ 导出Excel
              </b-button>
              <span class="small text-muted">共 {{ viewRows.length }} 件</span>
            </div>

            <!-- 批量完成·批量删除 — 체크한 건에만(2026-10-18 SYL #85) -->
            <div class="float-right mb-2 daiban-batch">
              <span v-if="picked.length" class="small mr-2">已选 {{ picked.length }}</span>
              <b-button v-if="canDelete" size="sm" variant="success" class="font-weight-bold" :disabled="!picked.length || batching || statusFilter === 'deleted'" @click="batchComplete">批量完成</b-button>
              <b-button size="sm" variant="danger" class="font-weight-bold ml-1" :disabled="!picked.length || batching || statusFilter === 'deleted'" @click="batchDelete">批量删除</b-button>
              <b-button v-if="canDelete && statusFilter === 'deleted'" size="sm" variant="outline-danger" class="font-weight-bold ml-1" :disabled="!picked.length || batching" @click="showPurge = true">彻底删除</b-button>
            </div>
            <!-- 처리방식 탭 — 退货만 / 补发만 따로 본다. 그 밖의 처리방식(退款·换货·客户废弃 등)과 미정은 全部에서 -->
            <div class="btn-group btn-group-sm mb-2 daiban-tabs">
              <button v-for="t in methodTabs" :key="t.value" type="button"
                      :class="['btn', methodTab === t.value ? 'btn-primary' : 'btn-outline-primary']"
                      @click="methodTab = t.value">{{ t.text }} <small>{{ tabCount(t.value) }}</small></button>
            </div>

            <!-- 代办确认 탭 3개(2026-10-16 SYL #47) — 처리진도 태그로 걸러 보는 필터 -->
            <div class="btn-group btn-group-sm mb-2 daiban-tabs">
              <button v-for="t in confirmTabs" :key="'ct' + t.value" type="button"
                      :class="['btn', confirmTab === t.value ? 'btn-warning' : 'btn-outline-warning']"
                      @click="confirmTab = t.value">{{ t.text }} <small>{{ confirmTabCount(t.value) }}</small></button>
            </div>

            <div v-if="loading" class="text-center my-4"><b-spinner variant="primary"></b-spinner></div>

            <div v-else class="table-responsive import-error-list">
              <table class="table table-sm table-bordered table-hover mb-0">
                <colgroup>
                  <col style="width:34px"><col style="width:84px"><col style="width:90px"><col style="width:140px"><col style="width:120px"><col style="width:260px">
                  <col style="width:160px"><col style="width:160px"><col style="width:90px"><col style="width:90px">
                </colgroup>
                <thead class="thead-light">
                  <tr>
                    <th class="text-center"><input type="checkbox" :checked="allPicked" @change="toggleAllPicked($event.target.checked)" title="全选"></th>
                    <th>登记日期</th>
                    <th>客户名字</th>
                    <th>订单号</th>
                    <th>错误类型</th>
                    <th>商品名</th>
                    <th>客户处理意见</th>
                    <th>办公室处理进度</th>
                    <th>创建人</th>
                    <th>代办明细</th>
                  </tr>
                  <tr class="daiban-colfilter">
                    <th></th>
                    <th><input v-model.trim="colFilter.date" class="form-control form-control-sm" placeholder="筛选"></th>
                    <th><input v-model.trim="colFilter.cust" class="form-control form-control-sm" placeholder="筛选"></th>
                    <th><input v-model.trim="colFilter.order" class="form-control form-control-sm" placeholder="筛选"></th>
                    <th><input v-model.trim="colFilter.etype" class="form-control form-control-sm" placeholder="筛选"></th>
                    <th><input v-model.trim="colFilter.prod" class="form-control form-control-sm" placeholder="筛选"></th>
                    <th><input v-model.trim="colFilter.opinion" class="form-control form-control-sm" placeholder="筛选"></th>
                    <th><input v-model.trim="colFilter.progress" class="form-control form-control-sm" placeholder="筛选"></th>
                    <th><input v-model.trim="colFilter.creator" class="form-control form-control-sm" placeholder="筛选"></th>
                    <th><b-button size="sm" variant="link" class="p-0" @click="clearColFilter">清除</b-button></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in viewRows" :key="r._id" :class="groupClass(r)">
<td class="text-center"><input v-model="picked" type="checkbox" :value="r._id" :disabled="!!r.삭제됨 && statusFilter !== 'deleted'"></td>
<td class="small">
                        <div>{{ r._등록일.slice(0, 10) }}</div>
                        <div class="text-muted">{{ r._등록일.slice(11) }}</div>
                        <div v-if="r.삭제됨" class="badge badge-secondary" :title="'删除: ' + (r.삭제자 || '') + ' ' + fmtDel(r.삭제일)">已删除</div>
                      </td>
<td class="import-error-list__wrap">
                        <div>{{ r.companyid }}</div>
                        <div class="small text-muted" title="小组(提交人的 分类)">{{ teamText(r) }}</div>
                      </td>
<td class="import-error-list__wrap small">
                        <div>
                          <a v-if="r._주문번호" :href="'https://trade.1688.com/order/new_step_order_detail.htm?orderId=' + r._주문번호" target="_blank" rel="noopener" title="在1688打开订单(需登录1688)">{{ r._주문번호 }}</a>
                        </div>
                        <div v-if="r.화주주문번호" class="text-muted">货主 {{ r.화주주문번호 }}</div>
                        <!-- 旺旺 열은 없애고 링크를 订单号 칸 맨 아래로(2026-10-16 SYL #43) -->
                        <div v-if="r._sellerOpenId"><a href="javascript:void(0)" @click="openWangwang(r)" :title="r.item && r.item.companyName">旺旺 link</a></div>
                        <span v-if="orderGroup[r._id] && orderGroup[r._id].size > 1" class="badge badge-info" title="同一订单号的商品放在一起(같은 주문번호 상품끼리 묶어서 보여 줍니다)">同订单 {{ orderGroup[r._id].n }}/{{ orderGroup[r._id].size }}</span>
                      </td>
<td class="import-error-list__wrap">
                        <div v-for="v in errorTypeValuesOf(r)" :key="'et' + v" class="mb-1">
                          <span class="daiban-etag" :style="errTypeStyle(v, errTypeAll)">{{ errorTypeText(v) }}</span>
                        </div>
                      </td>
                      <td>
                        <!-- 같은 订单号는 1행 — 상품마다 이미지·이름·옵션·바코드·수량을 줄줄이(2026-10-16 SYL #59). 높이는 첫 행 기준으로 통일하고 넘치면 접어 둔다(展开) (2026-10-18 SYL #80) -->
                        <div :class="{ 'daiban-prod-collapsed': !prodOpen[r._id] }">
                        <div v-for="(m, mi) in displayItems(r)" :key="'pr' + m._id" :class="mi ? 'border-top mt-1 pt-1' : ''">
                        <div class="d-flex" style="gap:4px">
                          <!-- 商品图片 클릭 = 商品链接(새 탭). 링크가 없으면 그림만 -->
                          <a v-if="m.구매링크 && (m.옵션이미지 || m.등록이미지)" :href="m.구매링크" target="_blank" rel="noopener" title="打开商品链接">
                            <img :src="m.옵션이미지 || m.등록이미지" class="import-error-list__pimg" alt="">
                          </a>
                          <img v-else-if="m.옵션이미지 || m.등록이미지" :src="m.옵션이미지 || m.등록이미지" class="import-error-list__pimg" alt="">
                          <a v-else-if="m.구매링크" :href="m.구매링크" target="_blank" rel="noopener" class="small">商品链接</a>
                          <div class="import-error-list__minw">
                            <div class="import-error-list__clamp" :title="m.상품명">{{ m.상품명 }}</div>
                            <div v-if="m._옵션" class="small text-danger import-error-list__wrap">{{ m._옵션 }}</div>
                            <div class="small text-muted import-error-list__wrap">{{ m.바코드 }}</div>
                          </div>
                        </div>
                        <div class="mt-1 text-nowrap" title="错误个数 / 总共下单个数"><strong class="text-danger">{{ foldQty(r, m) }}</strong> / {{ m._구매수량.total || '-' }} <span v-if="m._구매수량.total" class="text-muted">({{ m._구매수량.base }}x<span style="color:#ff0000;font-weight:900">{{ m._구매수량.p }}P</span>)</span><b-button v-if="mi === 0 && ((memberMap[r._id] || []).length || (r._옵션 || '').length > 60)" size="sm" variant="link" class="p-0 ml-2 font-weight-bold" @click="$set(prodOpen, r._id, !prodOpen[r._id])">{{ prodOpen[r._id] ? '收起 ▴' : '展开 ▾' }}</b-button></div>
                        </div>
                      </div>
                      </td>
                      <td class="import-error-list__black">
                        <!-- 客户处理意见 — 목록에서 바로 고른다(代办明细 팝업과 같은 값). 드롭다운을 닫을 때 바뀐 게 있으면 바로 저장(2026-10-16 SYL #50) -->
                        <b-dropdown size="sm" variant="outline-dark" block :disabled="!!r.삭제됨" :toggle-class="['import-error-list__tagbtn', tagColorClass(r._edit.처리방식태그, methodTagOptions.map(o => o.value))]" menu-class="import-error-list__tagmenu" boundary="viewport" @hidden="saveIfDirty(r)">
                          <template #button-content><span class="small">{{ tagSummary(r._edit.처리방식태그, r._edit.처리결과, 'method') }}</span></template>
                          <b-dropdown-form><b-form-checkbox-group v-model="r._edit.처리방식태그" :options="methodTagOptions" stacked></b-form-checkbox-group></b-dropdown-form>
                        </b-dropdown>
                        <div v-if="!r._edit.처리방식태그.length && r._edit.처리결과" class="small mt-1">{{ resultText(r._edit.처리결과) }}</div>
                      </td>
                      <td class="import-error-list__black">
                        <b-dropdown size="sm" variant="outline-dark" block :disabled="!!r.삭제됨" :toggle-class="['import-error-list__tagbtn', tagColorClass(r._edit.처리진도태그, progressTagOptions.map(o => o.value))]" menu-class="import-error-list__tagmenu" boundary="viewport" @hidden="saveIfDirty(r)">
                          <template #button-content><span class="small">{{ tagSummary(r._edit.처리진도태그, r._edit.처리진행, 'progress') }}</span></template>
                          <b-dropdown-form><b-form-checkbox-group v-model="r._edit.처리진도태그" :options="progressTagOptions" stacked></b-form-checkbox-group></b-dropdown-form>
                        </b-dropdown>
                        <div v-if="!r._edit.처리진도태그.length && r._edit.처리진행 && r._edit.처리진행 !== '대기'" class="small mt-1">{{ progressText(r._edit.처리진행) }}</div>
                      </td>
                      <td class="small import-error-list__wrap">
                        <!-- 创建人 = 오류를 등록한 직원(처리이력 첫 기록의 처리자) -->
                        <div>{{ r._등록자 || '-' }}</div>
                      </td>
                      <td class="text-center">
                        <b-button variant="outline-primary" class="px-2 py-0 font-weight-bold" style="font-size:13px" @click="openDaiban(r)">代办明细</b-button>
                      </td>
                  </tr>
                  <tr v-if="!viewRows.length">
                    <td colspan="11" class="text-center text-muted py-4">没有数据</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- @待确认消息 — 내 계정이 @된 미확인 회신(2026-10-20 SYL #120). 항목을 누르면 그 代办으로, 회신하거나 «确认» 를 누르면 사라진다 -->
    <b-modal v-model="showMentions" title="@待确认消息" size="lg" scrollable hide-footer>
      <div v-if="!mentionList.length" class="text-muted text-center py-4">没有待确认的消息</div>
      <div v-else>
        <div class="text-right mb-2"><b-button size="sm" variant="outline-secondary" :disabled="mentionBusy" @click="confirmMention(null)">全部确认</b-button></div>
        <div v-for="(m, i) in mentionList" :key="m.id" class="border rounded p-2 mb-2 d-flex align-items-start" style="gap:8px">
          <div class="font-weight-bold" style="min-width:24px">{{ i + 1 }}.</div>
          <div class="flex-grow-1" style="cursor:pointer" @click="openMention(m)">
            <div class="small text-muted">{{ m.from }} · {{ m.화주 }} · {{ m.주문번호 }} · {{ ymdhm(new Date(m.at).getTime()) }}</div>
            <div style="white-space:pre-wrap;word-break:break-all">{{ m.내용 }}</div>
          </div>
          <b-button size="sm" variant="outline-success" :disabled="mentionBusy" @click="confirmMention(m)">确认</b-button>
        </div>
      </div>
    </b-modal>

    <!-- 已删除 목록 彻底删除 확인창(#167) — 되돌릴 수 없다 -->
    <b-modal v-model="showPurge" title="彻底删除" centered :no-close-on-backdrop="batching">
      <div class="text-danger font-weight-bold">彻底删除所选 {{ picked.length }} 件? 删除后无法恢复!</div>
      <div class="small text-muted mt-1">(선택한 {{ picked.length }}건을 완전삭제합니다. 복구할 수 없습니다)</div>
      <template #modal-footer>
        <b-button size="sm" variant="secondary" :disabled="batching" @click="showPurge = false">取消</b-button>
        <b-button size="sm" variant="danger" :disabled="batching" @click="batchPurge"><b-spinner v-if="batching" small></b-spinner> 彻底删除</b-button>
      </template>
    </b-modal>

    <!-- ==================== 代办明细 팝업(2026-10-16 SYL #49) — 목록에 못 담은 칸과 편집·저장은 여기서 ==================== -->
    <b-modal v-model="showDaiban" size="xl" scrollable hide-footer @hide="saveIfDirty(daibanRow)" :modal-class="['daiban-popup', daibanRow && daibanRow.삭제됨 ? 'daiban-readonly' : '']" @hidden="daibanRow = null" no-close-on-backdrop>
      <!-- 제목줄: 代办明细 · 订单号码 · 客户名 [删除] [修改] (2026-10-18 SYL #79) -->
      <template #modal-title>
        <span v-if="daibanRow">代办明细 · {{ daibanRow._주문번호 || daibanRow.화주주문번호 || '' }} · {{ daibanRow.companyid || '' }}
          <!-- 删除·修改·完成代办 — 删除·取消 확인창 버튼과 같은 모양, 권한 없으면 버튼을 숨긴다(2026-10-19 SYL #87) -->
          <b-button v-if="canDelete" variant="danger" class="ml-3 align-baseline" :disabled="daibanRow._saving" @click="askDelete(daibanRow)">删除</b-button>
          <b-button v-if="daibanRow.수정가능" variant="warning" class="ml-2 align-baseline" :disabled="daibanRow._saving" @click="openEdit(daibanRow)">修改</b-button>
          <b-button v-if="canDelete && daibanRow.삭제됨" variant="primary" class="ml-2 align-baseline" :disabled="daibanRow._saving" @click="restoreDeleted(daibanRow)">恢复</b-button>
          <b-button v-if="canDelete && !daibanRow.삭제됨" variant="success" class="ml-2 align-baseline" :disabled="daibanRow._saving" @click="toggleComplete(daibanRow)">{{ isDaibanDone(daibanRow) ? '取消完成' : '✓ 完成' }}</b-button>
        </span>
      </template>
      <!-- 맨 위 요약: 登记日期·客户名字·订单号·商品名·代办内容(2026-10-16 SYL #58). 修改·删除는 하단 버튼을 없앤 대신 여기 -->
      <!-- 좌우 두 영역을 따로 스크롤(2026-10-26 SYL #155): 왼쪽 = 登记日期·客户名字·订单号 + 그 아래 商品名·原因·图片 + 确认内容 + 처리 칸들, 오른쪽 = 处理内容·处理记录 -->
      <div v-if="dr" class="row daiban-split">
      <div class="col-lg-7 daiban-pane">
      <div class="border rounded p-2 mb-2 daiban-head">
        <div class="row">
          <div class="col-4 mb-1">
            <div class="small font-weight-bold text-secondary">登记日期</div>

                        <div>{{ dr._등록일.slice(0, 10) }}</div>
                        <div class="text-muted">{{ dr._등록일.slice(11) }}</div>
                      
          </div>
          <div class="col-4 mb-1">
            <div class="small font-weight-bold text-secondary">客户名字</div>

                        <div>{{ dr.companyid }}</div>
                        <div class="small text-muted" title="小组(提交人的 分类)">{{ teamText(dr) }}</div>
                      
          </div>
          <div class="col-4 mb-1">
            <div class="small font-weight-bold text-secondary">订单号</div>

                        <div>
                          <a v-if="dr._주문번호" :href="'https://trade.1688.com/order/buyer_order_list.htm?orderId=' + encodeURIComponent(dr._주문번호)" target="_blank" rel="noopener" title="复制订单号并在1688搜索订单(需登录1688)" @click.prevent="open1688Order(dr._주문번호)">{{ dr._주문번호 }}</a>
                        </div>
                        <div v-if="dr.화주주문번호" class="text-muted">货主 {{ dr.화주주문번호 }}</div>
                        <!-- 旺旺 열은 없애고 링크를 订单号 칸 맨 아래로(2026-10-16 SYL #43) -->
                        <div v-if="dr._sellerOpenId"><a href="javascript:void(0)" @click="openWangwang(dr)" :title="dr.item && dr.item.companyName">旺旺 link</a></div>
                        <span v-if="orderGroup[dr._id] && orderGroup[dr._id].size > 1" class="badge badge-info" title="同一订单号的商品放在一起(같은 주문번호 상품끼리 묶어서 보여 줍니다)">同订单 {{ orderGroup[dr._id].n }}/{{ orderGroup[dr._id].size }}</span>
                      
          </div>
          <!-- 商品名 · 原因 — 옵션(건)마다 한 행으로 줄을 맞춘다(2026-10-18 SYL #72) -->
          <div class="col-12 mb-1">
            <div class="d-flex">
              <div class="small font-weight-bold text-secondary" style="flex:3">商品名</div>
              <div class="small font-weight-bold text-secondary pl-2" style="flex:2">原因</div>
              <div class="small font-weight-bold text-secondary pl-2" style="flex:2">图片</div>
            </div>
            <div v-for="(m, mi) in displayItems(dr)" :key="'pp' + m._id" class="d-flex" :class="mi ? 'border-top mt-1 pt-1' : ''">
              <div style="flex:3;min-width:0">
                        <div class="d-flex" style="gap:4px">
                          <!-- 商品图片 클릭 = 商品链接(새 탭). 링크가 없으면 그림만 -->
                          <a v-if="m.구매링크 && (m.옵션이미지 || m.등록이미지)" :href="m.구매링크" target="_blank" rel="noopener" title="打开商品链接">
                            <img :src="m.옵션이미지 || m.등록이미지" class="import-error-list__pimg" alt="">
                          </a>
                          <img v-else-if="m.옵션이미지 || m.등록이미지" :src="m.옵션이미지 || m.등록이미지" class="import-error-list__pimg" alt="">
                          <a v-else-if="m.구매링크" :href="m.구매링크" target="_blank" rel="noopener" class="small">商品链接</a>
                          <div class="import-error-list__minw">
                            <div class="import-error-list__clamp" :title="m.상품명">{{ m.상품명 }}</div>
                            <div v-if="m._옵션" class="small text-danger import-error-list__wrap">{{ m._옵션 }}</div>
                            <div class="small text-muted import-error-list__wrap">{{ m.바코드 }}</div>
                          </div>
                        </div>
                        <div class="mt-1 text-nowrap" title="错误个数 / 总共下单个数"><strong class="text-danger">{{ foldQty(dr, m) }}</strong> / {{ m._구매수량.total || '-' }} <span v-if="m._구매수량.total" class="text-muted">({{ m._구매수량.base }}x<span style="color:#ff0000;font-weight:900">{{ m._구매수량.p }}P</span>)</span></div>
              </div>
              <div class="pl-2" style="flex:2;min-width:0">
                <div v-if="reasonText(m)" style="white-space:pre-wrap;word-break:break-all;user-select:text" :title="reasonText(m)">{{ reasonText(m) }}</div>
                <div v-else class="text-muted">-</div>
                <div>可出库数量：{{ m._상세.출고가능수량 === undefined ? '' : m._상세.출고가능수량 }}</div>
                <div>不成组良品数量：{{ m._상세.미구성양품수량 === undefined ? '' : m._상세.미구성양품수량 }}</div>
                <div style="white-space:pre-wrap;word-break:break-all;user-select:text">是否已作业&作业内容：{{ m._상세.작업내용 || m._상세.작업내용 || '' }}</div>
              </div>
              <!-- 사진 — 이 옵션 행과 같은 높이에 독립 칸으로(2026-10-19 SYL #94) -->
              <div class="pl-2" style="flex:2;min-width:0">
                <div v-if="imageItemsFolded(dr, m).length" class="d-flex flex-wrap" style="gap:4px">
                  <a v-for="it in imageItemsFolded(dr, m)" :key="it.idx" href="javascript:void(0)" title="点击放大" @click="openPhotos(confirmImagesRow(dr), it.idx)">
                    <video v-if="isVideoUrl(it.url)" :src="it.url" preload="metadata" muted style="width:56px;height:56px;object-fit:cover"></video>
                    <img v-else :src="it.url" alt="" style="width:56px;height:56px;object-fit:cover" class="border rounded">
                  </a>
                </div>
                <div v-else class="text-muted">-</div>
              </div>
            </div>
          </div>
          <div class="col-12 mb-1">
            <div class="d-flex align-items-center" style="gap:8px">
              <span class="small font-weight-bold text-secondary">确认内容</span>
              <b-button variant="primary" class="px-3 py-0 font-weight-bold" style="font-size:15px" @click="copyConfirm(dr)">复制</b-button>
            </div>
            <!-- 确认内容(옛 代办内容) — 订单号 한 번, 아래로 옵션(같은 订单号·상품의 다른 건 #52 포함)마다 상품명·下单数量·原因. 复制 한 번에 전체(2026-10-18 SYL #72) -->
            <div v-if="dr._주문번호" style="user-select:text">{{ dr._주문번호 }}</div>
            <div v-for="(m, mi) in displayItems(dr)" :key="'dc' + m._id" :class="mi ? 'border-top mt-1 pt-1' : ''">
              <div style="white-space:pre-wrap;word-break:break-all;user-select:text" :title="m._옵션">{{ m._옵션 || '-' }}</div>
              <div>下单数量：{{ m._구매수량.total || '-' }}</div>
              <div style="white-space:pre-wrap;word-break:break-all;user-select:text">原因：{{ reasonText(m) || '-' }}</div>
            </div>
          </div>
          <!-- (옛) 오른쪽 이미지 칸 — 옵션별 사진을 확인내용 안으로 옮겨 숨김 -->
          <div v-if="false" class="col-md-4 mb-1">
            <div class="small font-weight-bold text-secondary">图片</div>
            <div v-for="(g, gi) in confirmImageGroups(dr)" :key="'ig' + gi" :class="gi ? 'mt-1' : ''">
              <div v-if="confirmImageGroups(dr).length > 1" class="small text-muted text-truncate" :title="g.label">{{ g.label }}</div>
              <div class="d-flex flex-wrap" style="gap:4px">
                <a v-for="it in g.items" :key="it.idx" href="javascript:void(0)" title="点击放大" @click="openPhotos(confirmImagesRow(dr), it.idx)">
                  <video v-if="isVideoUrl(it.url)" :src="it.url" preload="metadata" muted style="width:56px;height:56px;object-fit:cover"></video>
                  <img v-else :src="it.url" alt="" style="width:56px;height:56px;object-fit:cover" class="border rounded">
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
        <div class="daiban-left" @focusout="onLeftFocusout">
          <div class="border rounded p-2 mb-2 ">
            <div class="small font-weight-bold text-secondary mb-1">客户处理意见</div>

                        <!-- 处理方式 다중선택(2026-10-16 SYL #35) — 서버가 옛 처리결과(상단 탭·상세칸 기준)를 같이 맞춘다. 태그가 없는 옛 건은 옛 값을 글자로만 보여 준다 -->
                        <b-dropdown size="sm" variant="outline-secondary" block class="mb-1" :toggle-class="['import-error-list__tagbtn', tagColorClass(dr._edit.처리방식태그, methodTagOptions.map(o => o.value))]" menu-class="import-error-list__tagmenu" boundary="viewport">
                          <template #button-content><span class="small">{{ tagSummary(dr._edit.처리방식태그, dr._edit.처리결과, 'method') }}</span></template>
                          <b-dropdown-form><b-form-checkbox-group v-model="dr._edit.처리방식태그" :options="methodTagOptions" stacked></b-form-checkbox-group></b-dropdown-form>
                        </b-dropdown>
                        <div v-if="!dr._edit.처리방식태그.length && dr._edit.처리결과" class="small text-muted mb-1">{{ resultText(dr._edit.처리결과) }}</div>
                        
                      
          </div>
          <div class="border rounded p-2 mb-2 ">
            <div class="small font-weight-bold text-secondary mb-1">办公室处理进度</div>

                        <!-- 处理进度 다중선택(2026-10-16 SYL #36) — 서버가 처리완료/처리중/대기를 같이 맞춘다. 태그가 없는 옛 건은 옛 값을 글자로만 보여 준다 -->
                        <b-dropdown size="sm" variant="outline-secondary" block :toggle-class="['import-error-list__tagbtn', tagColorClass(dr._edit.처리진도태그, progressTagOptions.map(o => o.value))]" menu-class="import-error-list__tagmenu" boundary="viewport">
                          <template #button-content><span class="small">{{ tagSummary(dr._edit.처리진도태그, dr._edit.처리진행, 'progress') }}</span></template>
                          <b-dropdown-form><b-form-checkbox-group v-model="dr._edit.처리진도태그" :options="progressTagOptions" stacked></b-form-checkbox-group></b-dropdown-form>
                        </b-dropdown>
                        <div v-if="!dr._edit.처리진도태그.length && dr._edit.처리진행 && dr._edit.처리진행 !== '대기'" class="small text-muted mt-1">{{ progressText(dr._edit.처리진행) }}</div>
                      
          </div>
          <div class="border rounded p-2 mb-2 ">
            <div class="small font-weight-bold text-secondary mb-1">仓储进度</div>

                        <!-- 仓储进度 단일선택(2026-10-16 SYL #46) — 예전 «当前状态»(시스템 상태)는 지우지 않고 아래 작은 글씨로 계속 보인다 -->
                        <b-form-select v-model="dr._edit.창고진도" :options="warehouseOptions" :style="optStyle(dr._edit.창고진도, warehouseOptions)" size="sm" class="px-1 mb-1 import-error-list__tagbtn"></b-form-select>
                        
                        <!-- 입고처리 화면에서 정한 처리방식(退货·废弃 등) — 등록만 된 상태(已登记)는 따로 표시하지 않는다 -->
                        <b-badge v-if="dr._상세.오류상태 && dr._상세.오류상태 !== '접수'" :variant="errorStatusVariant(dr._상세.오류상태)">{{ errorStatusText(dr._상세.오류상태) }}</b-badge>
                        <!-- 입고 조정(입고처리 화면 처리 선택) — 고른 선택지. 되돌리기는 입고처리 화면 「调整」 -->
                        <div v-if="dr._상세.조정" class="text-info import-error-list__wrap" :title="dr._상세.조정.요약">{{ dr._상세.조정.라벨 }}</div>
                      
          </div>
          <div v-if="drNeedsShip || drNeedsReturn" class="border rounded p-2 mb-2">
            <div class="small font-weight-bold text-secondary mb-1">退货/退款/补发/换货 信息</div>
            <!-- 退货·退款·补发·换货 에 맞는 입력칸만 보인다(2026-10-16 SYL #49) — 고른 客户处理意见 에서 바로 정해진다 -->
            <div v-if="drNeedsShip" class="form-group mt-2 mb-1">
              <label class="small mb-1">补发单号</label>
              <b-input-group size="sm">
                <input v-model.trim="dr._edit.재발송운송장번호" type="text" class="form-control form-control-sm" placeholder="补发单号">
                <b-input-group-append><b-button size="sm" variant="outline-secondary" :disabled="!dr._edit.재발송운송장번호" @click="trackParcel(dr._edit.재발송운송장번호, 'baidu')">物流追踪</b-button></b-input-group-append>
              </b-input-group>
            </div>
            <div v-if="drNeedsReturn" class="form-row mt-2">
              <div class="form-group col-md-6">
                <label class="small mb-1">退货单号(小件)</label>
                <b-input-group size="sm">
                  <input v-model.trim="dr._edit.반품.운송장번호" type="text" class="form-control form-control-sm">
                  <b-input-group-append><b-button size="sm" variant="outline-secondary" :disabled="!dr._edit.반품.운송장번호" @click="trackParcel(dr._edit.반품.운송장번호, 'baidu')">物流追踪</b-button></b-input-group-append>
                </b-input-group>
              </div>
              <div class="form-group col-md-6">
                <label class="small mb-1">退货重量(kg)(仓库)</label>
                <input v-model.trim="dr._edit.반품.창고중량표기" type="text" class="form-control form-control-sm">
              </div>
              <div class="form-group col-md-6">
                <label class="small mb-1">大件退货单号</label>
                <b-input-group size="sm">
                  <input v-model.trim="dr._edit.반품.대형운송장번호" type="text" class="form-control form-control-sm">
                  <b-input-group-append><b-button size="sm" variant="outline-secondary" :disabled="!dr._edit.반품.대형운송장번호" @click="trackParcel(dr._edit.반품.대형운송장번호, 'baidu')">物流追踪</b-button></b-input-group-append>
                </b-input-group>
              </div>
              <div class="form-group col-md-6">
                <label class="small mb-1">退款金额(人民币)</label>
                <input v-model="dr._edit.반품.환불금액" type="number" min="0" step="0.01" class="form-control form-control-sm">
              </div>
              <div class="form-group col-md-6">
                <label class="small mb-1">运费金额(人民币)</label>
                <input v-model="dr._edit.반품.운임금액" type="number" min="0" step="0.01" class="form-control form-control-sm">
              </div>
              <div class="form-group col-md-6">
                <label class="small mb-1">运费承担方式</label>
                <b-form-select v-model="dr._edit.반품.운임부담" :options="freightOptsFor(dr._edit.반품.운임부담)" :style="optStyle(dr._edit.반품.운임부담, freightOptsFor(dr._edit.반품.운임부담))" size="sm"></b-form-select>
              </div>
              <div class="form-group col-md-6">
                <!-- 숫자만 쳐도 되는 입력(20261017 → 2026-10-17). 라벨 바로 오른쪽에 입력칸(2026-10-18 SYL #72) -->
                <div>
                  <label class="small mb-1 text-nowrap">运费到账时间</label>
                  <input :value="freightDateText(dr)" type="text" maxlength="10" class="form-control form-control-sm" @input="onFreightDateInput(dr, $event)" @blur="onFreightDateBlur(dr)">
                </div>
              </div>
              <div class="form-group col-md-6">
                <label class="small mb-1">办理进度</label>
                        <b-dropdown size="sm" variant="outline-secondary" block :toggle-class="['import-error-list__tagbtn', tagColorClass(dr._edit.办理진도태그, handleTagOptions.map(o => o.value))]" menu-class="import-error-list__tagmenu" boundary="viewport">
                          <template #button-content><span class="small">{{ (dr._edit.办理진도태그 && dr._edit.办理진도태그.length) ? dr._edit.办理진도태그.join(' / ') : '办理进度(选择)' }}</span></template>
                          <b-dropdown-form><b-form-checkbox-group v-model="dr._edit.办理진도태그" :options="handleTagOptions" stacked></b-form-checkbox-group></b-dropdown-form>
                        </b-dropdown>
                      
              </div>
              <div class="form-group col-12">
                <label class="small mb-1">退货地址</label>
                <textarea v-model="dr._edit.반품.주소" rows="2" class="form-control form-control-sm"></textarea>
              </div>
              <div v-if="false" class="form-group col-12 mb-0">
                <label class="small mb-1">仓储问题统计</label>
                <b-form-select v-model="dr._edit.반품.창고문제통계" :options="storageIssueOptions" :style="optStyle(dr._edit.반품.창고문제통계, storageIssueOptions)" size="sm"></b-form-select>
              </div>
            </div>
          </div>
          <div v-if="!dr.삭제됨 && (isDirty(dr) || dr._saving)" class="mb-2">
            <b-button variant="primary" class="px-4" :disabled="!isDirty(dr) || dr._saving" @click="saveRowGroup(dr)"><b-spinner v-if="dr._saving" small></b-spinner> 保存</b-button>
            <span v-if="isDirty(dr)" class="small text-danger ml-2">有未保存的修改</span>
          </div>
          <!-- 仓储问题统计 — 退货/退款/补发/换货 信息 칸에서 분리, 자기 칸(2026-10-18 SYL #83) -->
          <div class="border rounded p-2 mb-2">
            <label class="small mb-1 font-weight-bold" style="color:#f97316">仓储问题统计</label>
            <b-form-select v-model="dr._edit.반품.창고문제통계" :options="storageIssueOptions" :style="optStyle(dr._edit.반품.창고문제통계, storageIssueOptions)" size="sm"></b-form-select>
          </div>
        </div>
      </div>
        <div class="col-lg-5 daiban-pane daiban-right">
          <div class="border rounded p-2 mb-2 ">
            <!-- 处理内容 ↔ 处理记录 탭 전환(2026-10-18 SYL #70) — 回复는 시간순으로 쌓이고(맨 아래 = 최신) 입력창은 아래에 고정 -->
            <div class="d-flex mb-1" style="gap:6px">
              <b-button :variant="dr._tab === 'history' ? 'outline-secondary' : 'secondary'" class="px-3 py-1" style="font-size:16px" @click="$set(dr, '_tab', 'content')">处理内容</b-button>
              <b-button :variant="dr._tab === 'history' ? 'secondary' : 'outline-secondary'" class="px-3 py-1" style="font-size:16px" @click="$set(dr, '_tab', 'history')">处理记录</b-button>
            </div>
            <div v-show="dr._tab !== 'history'">
              <div v-if="repliesOf(dr).length" class="font-weight-bold mb-1" style="font-size:16px">已回复</div>
              <div v-if="repliesOf(dr).length" class="border rounded p-2 mb-1" style="display:flex;flex-direction:column-reverse;max-height:62vh;min-height:200px;overflow-y:auto;font-size:15px">
                <div v-for="(a, ai) in repliesOf(dr)" :key="ai" class="border-top pt-1 mt-1 import-error-list__wrap">
                  <div style="white-space:pre-wrap"><b>{{ a.작성자 || '-' }}</b>  {{ a.내용 }}</div>
                  <div v-if="a.첨부 && a.첨부.length" class="d-flex flex-wrap mt-1" style="gap:4px">
                    <a v-for="(u, ui) in a.첨부" :key="ui" :href="u" target="_blank" rel="noopener">
                      <video v-if="isVideoUrl(u)" :src="u" preload="metadata" muted style="width:56px;height:56px;object-fit:cover"></video>
                      <img v-else :src="u" alt="" style="width:56px;height:56px;object-fit:cover" class="border rounded">
                    </a>
                  </div>
                  <div class="text-muted" style="font-size:11px">{{ a.시각 ? ymdhm(a.시각) : '' }}<a v-if="a.시각 && !dr.삭제됨" href="javascript:void(0)" class="text-danger ml-2" @click="deleteReply(dr, a)">删除</a></div>
                </div>
              </div>
              <div class="daiban-reply-box">
              <!-- 첨부 — 사진·영상(여러 장). 回复를 저장할 때 같이 올라간다 -->
              <div class="d-flex flex-wrap align-items-center mt-1" style="gap:6px">
                <label class="btn btn-sm btn-outline-secondary mb-0">📎 附件<input type="file" multiple accept="image/*,video/*" style="display:none" :disabled="dr._upl" @change="onReplyFiles(dr, $event)"></label>
                <b-spinner v-if="dr._upl" small></b-spinner>
                <span v-for="(u, ui) in dr._replyFiles" :key="ui" class="position-relative" style="width:48px;height:48px">
                  <video v-if="isVideoUrl(u)" :src="u" preload="metadata" muted style="width:48px;height:48px;object-fit:cover"></video>
                  <img v-else :src="u" alt="" style="width:48px;height:48px;object-fit:cover" class="border rounded">
                  <b-button size="sm" variant="danger" class="position-absolute py-0 px-1" style="top:-4px;right:-4px;line-height:1.1" @click="dr._replyFiles.splice(ui, 1)">×</b-button>
                </span>
              </div>
              <div style="position:relative">
                <textarea v-model="dr._edit.새답글" rows="10" style="resize:vertical" class="form-control form-control-sm px-1" placeholder="添加回复 — 输入@提及同事 · 按 Enter 保存(Shift+Enter 换行)" @input="onReplyInput(dr, $event)" @keydown.enter.exact.prevent="onReplyEnter(dr)" @keydown.esc="$set(dr, '_mq', null)"></textarea>
                <div v-if="mentionHits(dr).length" class="border rounded bg-white shadow-sm" style="position:absolute;left:0;right:0;bottom:100%;z-index:5;max-height:180px;overflow-y:auto">
                  <div v-for="x in mentionHits(dr)" :key="x.login" class="small px-2 py-1" style="cursor:pointer" @mousedown.prevent="pickMention(dr, x)">@{{ x.name }}</div>
                </div>
              </div>
              </div>
            </div>
            <div v-show="dr._tab === 'history'">
<!-- 한 줄만 보이게 접고, 누르면 그 기록 전체(2026-10-19 SYL #87) -->
<div v-for="(line, li) in histLines(dr)" :key="'hl' + li" :style="Object.assign({ cursor: 'pointer', fontSize: '12px', fontFamily: '\'Microsoft YaHei\', sans-serif' }, (dr._histOpen && dr._histOpen[li]) ? { whiteSpace: 'pre-wrap', wordBreak: 'break-all' } : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' })" class="border-bottom py-1" title="点击展开/收起" @click="$set(dr, '_histOpen', Object.assign({}, dr._histOpen, { [li]: !(dr._histOpen && dr._histOpen[li]) }))">{{ line }}</div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- 처리방식 상세 — 商家补发: 补发单号 / 退货·换货: 退货 8칸. 표 가로 스크롤과 상관없이 보이게 모달로 연다 -->
    <b-modal v-model="showDetail" :title="detailRow ? methodText(methodOf(detailRow)) + ' · ' + (detailRow.상품명 || '') : ''" size="lg" @hidden="detailRow = null">
      <div v-if="detailRow">
        <div class="small text-muted mb-2">{{ detailRow._주문번호 }} · {{ errorTypeText(detailRow._상세.오류유형) }} · 错误个数 {{ detailRow._오류수량 || '-' }}</div>
        <div v-if="methodOf(detailRow) === '판매자재발송'" class="form-group mb-0">
          <label class="small mb-1">补发单号</label>
          <input v-model.trim="detailRow._edit.재발송운송장번호" type="text" class="form-control" placeholder="补发单号">
        </div>
        <div v-else class="form-row">
          <div class="form-group col-12">
            <label class="small mb-1">退货地址</label>
            <input v-model="detailRow._edit.반품.주소" type="text" class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">退货单号(小件)</label>
            <input v-model.trim="detailRow._edit.반품.운송장번호" type="text" class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">大件退货单号</label>
            <input v-model.trim="detailRow._edit.반품.대형운송장번호" type="text" class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">退货重量(kg)</label>
            <input v-model="detailRow._edit.반품.중량" type="number" min="0" step="0.01" class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">长*宽*高(cm)</label>
            <input v-model="detailRow._edit.반품.규격식" type="text" placeholder="50*40*30" class="form-control form-control-sm" @input="recalcVolume(detailRow._edit.반품)">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">快递类型</label>
            <b-form-select v-model="detailRow._edit.반품.택배사구분" :options="carrierOptions" size="sm" @change="recalcVolume(detailRow._edit.반품)"></b-form-select>
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">体积重量(kg)</label>
            <input :value="detailRow._edit.반품.체적중량" type="text" readonly class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">退款金额(人民币)</label>
            <input v-model="detailRow._edit.반품.환불금액" type="number" min="0" step="0.01" class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">运费金额(人民币)</label>
            <input v-model="detailRow._edit.반품.운임금액" type="number" min="0" step="0.01" class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">运费承担方式</label>
            <b-form-select v-model="detailRow._edit.반품.운임부담" :options="freightOptsFor(detailRow._edit.반품.운임부담)" :style="optStyle(detailRow._edit.반품.운임부담, freightOptsFor(detailRow._edit.반품.운임부담))" size="sm"></b-form-select>
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">运费到账时间</label>
            <input v-model="detailRow._edit.반품.운임입금일" type="date" class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-4">
            <label class="small mb-1">单号录入方</label>
            <b-form-select v-model="detailRow._edit.반품.운송장입력주체" :options="waybillOwnerOptions" size="sm"></b-form-select>
          </div>
          <div class="form-group col-md-4" :class="{ 'mb-0': detailRow._edit.반품.운송장발급방식 !== 'A4수기' }">
            <label class="small mb-1">出单方式</label>
            <b-form-select v-model="detailRow._edit.반품.운송장발급방식" :options="waybillModeOptions" size="sm"></b-form-select>
          </div>
          <div v-if="detailRow._edit.반품.운송장발급방식 === 'A4수기'" class="form-group col-12 mb-0">
            <label class="small mb-1">取件码 / 承运公司 / 退货地址</label>
            <textarea v-model="detailRow._edit.반품.수기운송장정보" rows="2" maxlength="500" class="form-control form-control-sm"></textarea>
          </div>
        </div>
        <div v-if="methodOf(detailRow) === '교환'" class="form-group mt-3 mb-0 border-top pt-2">
          <label class="small mb-1">补发单号</label>
          <input v-model.trim="detailRow._edit.재발송운송장번호" type="text" class="form-control form-control-sm" placeholder="补发单号">
        </div>
      </div>
      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">关闭</b-button>
        <b-button variant="primary" :disabled="!detailRow || !isDirty(detailRow) || detailRow._saving" @click="saveDetail">
          <b-spinner v-if="detailRow && detailRow._saving" small></b-spinner> 保存
        </b-button>
      </template>
    </b-modal>

    <!-- 등록 내용 수정 — 등록한 본인만(서버가 다시 확인한다) -->
    <!-- 사진 확대 — 좌우 이동 + 전체 zip 다운로드 -->
    <b-modal v-model="showPhotos" size="xl" centered hide-footer title="图片" @hidden="photoRow = null">
      <div v-if="photoRow" class="text-center">
        <div class="d-flex align-items-center justify-content-center" style="gap:8px">
          <b-button variant="outline-primary" class="daiban-arrow" :disabled="photoIdx <= 0" @click="photoIdx--">‹</b-button>
          <video v-if="isVideoUrl(photoRow._이미지[photoIdx])" :key="photoIdx" :src="photoRow._이미지[photoIdx]" controls autoplay style="max-width:100%;max-height:70vh"></video>
          <img v-else :src="photoRow._이미지[photoIdx]" alt="" style="max-width:100%;max-height:70vh">
          <b-button variant="outline-primary" class="daiban-arrow" :disabled="photoIdx >= photoRow._이미지.length - 1" @click="photoIdx++">›</b-button>
        </div>
        <div class="small text-muted my-2">{{ photoIdx + 1 }} / {{ photoRow._이미지.length }}</div>
        <b-button size="sm" variant="success" :disabled="zipping" @click="downloadPhotos(photoRow)">
          <b-spinner v-if="zipping" small></b-spinner> ⬇ 全部下载(zip)
        </b-button>
        <div v-if="photoFallback" class="small mt-2 text-danger">
          无法一次性下载，请逐张打开后另存：
          <a v-for="(u, i) in photoRow._이미지" :key="i" :href="u" target="_blank" rel="noopener" class="mx-1">图{{ i + 1 }}</a>
        </div>
      </div>
    </b-modal>

    <b-modal v-model="showEdit" size="lg" scrollable title="修改错误登记 · 入库错误登记" @hidden="editTarget = null">
      <div v-if="editTarget">
        <!-- 옵션이 여러 개면 제목 아래에서 눌러 가며 고친다(2026-10-18 SYL #73) -->
        <div class="mb-2"><strong>{{ editTarget.상품명 }}</strong></div>
        <div v-if="editGroup.length > 1" class="d-flex flex-wrap mb-2" style="gap:6px">
          <b-button v-for="g in editGroup" :key="'eg' + g._id" size="sm" :variant="g._id === editTarget._id ? 'primary' : 'outline-secondary'" :disabled="editing" @click="switchEdit(g)">{{ g._옵션 || g.바코드 || '-' }}<span v-if="editDirtyOf(g)" class="text-danger"> ●</span></b-button>
        </div>
        <div class="form-group">
          <label class="small mb-1">错误类型<small>(오류유형)</small></label>
          <b-form-select v-model="editForm.오류유형" :options="editTypeOptions" class="err-type-select" :style="errTypeStyle(editForm.오류유형, editTypeOptions)" size="sm" :disabled="(editTarget._상세.오류상태 || '접수') !== '접수'"></b-form-select>
          <small v-if="(editTarget._상세.오류상태 || '접수') !== '접수'" class="text-muted">已处理的错误不能改类型(처리 방법을 고른 건은 유형 수정 불가)</small>
        </div>
        <div class="form-group">
          <label class="small mb-1">错误数量</label>
          <input v-model.number="editForm.오류수량" type="number" min="1" step="1" class="form-control form-control-sm" :disabled="!!editTarget.부분입고원본id">
          <small v-if="editTarget.부분입고원본id" class="text-muted">已拆分的错误不能改数量，请删除后重新登记</small>
        </div>
        <div class="form-group">
          <label class="small mb-1">错误内容</label>
          <textarea v-model="editForm.오류내용" rows="3" class="form-control form-control-sm"></textarea>
        </div>
        <div class="form-row">
          <div class="form-group col-md-6">
            <label class="small mb-1">可出库数量<small>(출고가능수량)</small></label>
            <input v-model.number="editForm.출고가능수량" type="number" min="0" step="1" class="form-control form-control-sm">
          </div>
          <div class="form-group col-md-6">
            <label class="small mb-1">不成组良品数量<small>(미구성 양품수량)</small></label>
            <input v-model.trim="editForm.미구성양품수량" type="text" maxlength="100" class="form-control form-control-sm">
          </div>
        </div>
        <div class="form-group">
          <label class="small mb-1">是否已作业&作业内容<small>(작업여부&작업내용)</small></label>
          <textarea v-model="editForm.작업내용" rows="2" maxlength="500" class="form-control form-control-sm" style="max-width:360px"></textarea>
        </div>
        <div class="form-group mb-0">
          <label class="small mb-1">图片/视频<small>(이미지·영상)</small></label>
          <div class="d-flex align-items-center mb-1" style="gap:8px">
            <input type="file" multiple accept="image/*,video/*" class="form-control-file" :disabled="editUploading" @change="onEditFiles">
            <b-spinner v-if="editUploading" small></b-spinner>
          </div>
          <div class="d-flex flex-wrap" style="gap:6px">
            <div v-for="(u, ui) in editForm.이미지" :key="u + ui" class="position-relative border rounded" style="width:96px;height:72px;overflow:hidden;background:#f1f3f5">
              <video v-if="isVideoUrl(u)" :src="u" preload="metadata" style="width:100%;height:100%;object-fit:cover"></video>
              <a v-else :href="u" target="_blank" rel="noopener"><img :src="u" alt="" style="width:100%;height:100%;object-fit:cover"></a>
              <b-button size="sm" variant="danger" class="position-absolute py-0 px-1" style="top:2px;right:2px;line-height:1.2" title="删除" @click="editForm.이미지.splice(ui, 1)">×</b-button>
            </div>
          </div>
        </div>
      </div>
      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">取消</b-button>
        <b-button variant="primary" :disabled="editing" @click="confirmEdit">
          <b-spinner v-if="editing" small></b-spinner> 保存
        </b-button>
      </template>
    </b-modal>

    <!-- 삭제 확인 — 페이지 안 모달 -->
    <b-modal v-model="showDelete" title="删除入库错误" @hidden="deleteTarget = null">
      <div v-if="deleteTarget">
        <div class="mb-2"><strong>{{ deleteTarget.상품명 }}</strong> <span class="text-danger">{{ deleteTarget._옵션 }}</span></div>
        <div class="mb-2">{{ errorTypeText(deleteTarget._상세.오류유형) }} · 错误个数 {{ deleteTarget._오류수량 || '-' }}</div>
        <div class="small text-muted">删除后该错误记录消失。当前为「入库错误」时恢复为登记前的状态；如果是从原单拆出的错误数量，会合并回原单。</div>
      </div>
      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">取消</b-button>
        <b-button variant="danger" :disabled="deleting" @click="confirmDelete">
          <b-spinner v-if="deleting" small></b-spinner> 删除
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { coupassMenuTitle } from './coupassMenu';
import axios from 'axios';
import { uploadImportPhoto } from '@/utils/importPhotoS3';
import * as XLSX from 'xlsx';
import { processStatusText, errorStatusText } from '@/utils/coupassStatusText';

// 입고처리(logisaddimportV0) 오류 등록 창과 같은 유형 — 값(저장 키)은 그대로, 표기만 중국어
// 错误类型 13종 + 예전 표기(2026-10-18) — 앞 13개만 새로 고를 수 있고, 뒤쪽은 이미 등록된 건의 표기·필터용
const ERROR_TYPES_MAIN = [
  { value: '수량부족', text: '数量不足' },
  { value: '수량초과', text: '数量超出' },
  { value: '상품불량', text: '商品破损/不良' },
  { value: '오배송', text: '商家发错货' },
  { value: '옵션뒤바뀜', text: '选项错发·多少互换' },
  { value: '与客户确认事项', text: '与客户确认事项' },
  { value: '与商家确认事项', text: '与商家确认事项' },
  { value: '条码问题', text: '条码问题' },
  { value: '此待办作废', text: '此待办作废' },
  { value: '到货少包裹，需要核实', text: '到货少包裹，需要核实' },
  { value: '退款/客户取消', text: '退款/客户取消' },
  { value: '未发货与客户确认事项', text: '未发货与客户确认事项' },
  { value: '办公室转达退款问题', text: '办公室转达退款问题' }
];
const ERROR_TYPES_LEGACY = [
  { value: '상품파손', text: '商品破损' },
  { value: '화주취소', text: '到货后客户取消' },
  { value: '到货产品问题', text: '到货产品问题' },
  { value: '无条码', text: '无条码' },
  { value: '기타', text: '其他' }
];
const ERROR_TYPES = [{ value: '', text: '全部' }].concat(ERROR_TYPES_MAIN, ERROR_TYPES_LEGACY);
// 处理方式·处理进度 다중선택 태그(2026-10-16 SYL #35·#36) — 서버 inventory-services/import-error-tags.js 와 같은 목록·순서
const METHOD_TAGS = ['待办信息不全待修改', '待办有问题暂时不要问', '分批发货多个包裹', '等客户回复', '退货退款', '补发件良品出库', '补发件保管', '少不良退款', '良品正常出库', '换货件良品出库', '换货件保管', '补发', '成组退款', '补偿差额退款', '正常出库', '不良品废弃', '不成组保管', '良品保管', '不良品保管', '条码已改', '退款客户取消', '余品小屋保管', '未问客户商家补发假日处理待办'];
const PROGRESS_TAGS = ['等补发单号', '补发到货待验收', '待办有问题待解决', '等待商家回复', '1688已办理', '随下次订单补发', '商家超时拒绝退款', '退货单号已填', '完成代办', '等顾客回复', '待仓库确认', '无条码已挪表', '疑难待处理', '暂不退货仓库先保管', '久未回复速办', '待办回复不明确/不予办理', '暂未处理退款退货', '*', '仓库验货作业后办理', '商家年后处理', '补发验收已逾期', '仓库找货验收后处理', '待采购确认'];
// 서버 import-error-tags.js 의 deriveResult·deriveProgress 와 같은 규칙(저장 직후 화면을 서버 값과 맞추려고)
const METHOD_TO_RESULT = { '退货退款': '반품', '换货件良品出库': '교환', '换货件保管': '교환', '补发件良品出库': '판매자재발송', '补发件保管': '판매자재발송', '补发': '판매자재발송', '未问客户商家补发假日处理待办': '판매자재발송', '少不良退款': '환불', '成组退款': '환불', '补偿差额退款': '환불', '退款客户取消': '환불', '不良品废弃': '양보수령' };
function deriveResult(tags) {
  if (!tags || !tags.length) return '';
  const hit = new Set(tags.map(t => METHOD_TO_RESULT[t]).filter(Boolean));
  const found = ['반품', '교환', '판매자재발송', '환불', '양보수령'].find(r => hit.has(r));
  return found || '기타';
}
function deriveProgress(tags) {
  if (!tags || !tags.length) return '대기';
  return tags.includes('完成代办') ? '처리완료' : '처리중';
}
// 仓储进度(단일선택)·办理进度(다중선택) 목록(2026-10-16 SYL #46) — 서버 inventory-services/import-error-tags.js 와 같은 목록
const WAREHOUSE_PROGRESS = ['补发验收完成', '补发验货异常', '仓库完成退货', '良品已出库·不良已退货', '良品已出库·不良暂未退货', '良品暂未出库·不良已退货'];
const HANDLE_TAGS = ['表三未登记', '表三已登记', '已登记·未到账', '不用登记', '待办追加购买', '待办重拍待退款', '待办重拍退款已到账', '补发件已定位', '补发已扫码入库', '补发签收未扫码', '等退货单号', '补发已送组上·待验收'];
// 代办确认 탭 3개(2026-10-16 SYL #47) — 처리진도 태그(번호는 PROGRESS_TAGS 의 번호)로 걸러 보는 필터. 새 상태값 아님
//   客户: 10.等顾客回复 · 18.*   商家: 4.等待商家回复 · 3.待办有问题待解决 · 13.疑难待处理 · 7.商家超时拒绝退款   仓储: 11.待仓库确认 · 22.仓库找货验收后处理 · 19.仓库验货作业后办理
const CONFIRM_TABS = [
  { value: '', text: '全部' },
  { value: 'cust', text: '客户确认', tags: ['等顾客回复', '*'] },
  { value: 'merchant', text: '商家确认', tags: ['等待商家回复', '待办有问题待解决', '疑难待处理', '商家超时拒绝退款'] },
  { value: 'store', text: '仓储确认', tags: ['待仓库确认', '仓库找货验收后处理', '仓库验货作业后办理'] }
];
const tagOptions = list => list.map((t, i) => ({ value: t, text: (i + 1) + '. ' + t }));
// 처리 진행(서버 IMPORT_ERROR_PROGRESS). 값이 없는 옛 건 = 대기
const PROGRESS = [
  { value: '대기', text: '待处理' },
  { value: '처리중', text: '处理中' },
  { value: '처리완료', text: '处理完成' }
];
// 처리방식(处理方式) — 저장 키는 처리결과(서버 IMPORT_ERROR_RESULTS). 순서·이름은 YZY 기준
const RESULTS = [
  { value: '', text: '-' },
  { value: '판매자재발송', text: '商家补发' },
  { value: '교환', text: '换货' },
  { value: '반품', text: '退货' },
  { value: '환불', text: '退款' },
  // 저장 키 양보수령 은 그대로(기존 데이터 호환), 표기만 「客户废弃」(2026-10-03 YZY)
  { value: '양보수령', text: '客户废弃' },
  { value: '기타', text: '其他' }
];
// 운임 부담(서버 IMPORT_ERROR_FREIGHT_PAYERS)
const FREIGHT_PAYERS_NEW = [
  { value: '', text: '-' },
  { value: '商家支付宝打款运费', text: '商家支付宝打款运费' },
  { value: '客户承担运费(退款扣除)', text: '客户承担运费(退款扣除)' },
  { value: '客户承担运费(账单付款)', text: '客户承担运费(账单付款)' },
  { value: '退货包运费', text: '退货包运费' },
  { value: '商家支付宝打款', text: '商家支付宝打款' },
  { value: '商家小额打款', text: '商家小额打款' },
  { value: '商家上门取件', text: '商家上门取件' },
  { value: '商家订单申请运费', text: '商家订单申请运费' },
  { value: '商家运费到付', text: '商家运费到付' },
  { value: '拒收', text: '拒收' },
  { value: '我们承担', text: '我们承担' }
];
// 예전 값(2026-10-18 이전 저장분) — 새로 고를 수는 없고 표기만 한다
const FREIGHT_PAYERS_OLD = [
  { value: '상가부담', text: '商家承担' },
  { value: '자사부담', text: '我方承担' },
  { value: '고객부담', text: '客户承担' },
  { value: '반품포함운임', text: '退货包运费' },
  { value: '기타', text: '其他' }
];
const FREIGHT_PAYERS = FREIGHT_PAYERS_NEW.concat(FREIGHT_PAYERS_OLD);
// 打款明细 칸이 붙는 값 — «退货包运费»(새 값·예전 값)
const FREIGHT_WITH_RETURN = ['退货包运费', '반품포함운임'];
// 반품 운송장 구분·지급내역(서버 IMPORT_ERROR_RETURN_ENUMS) — 운송장은 항상 사람이 입력한다
const WAYBILL_OWNERS = [{ value: '', text: '-' }, { value: '창고직원', text: '仓库员工' }, { value: '대행조', text: '代办组(1688上门取件)' }];
const WAYBILL_MODES = [{ value: '', text: '-' }, { value: 'A4수기', text: 'A4手写' }, { value: '신통자체', text: '申通自己打印' }];
const PAY_DETAILS = [{ value: '', text: '-' }, { value: '상품금액운임', text: '商品金额+运费' }, { value: '운임만', text: '仅运费' }];
const CARRIERS = [{ value: '', text: '-' }, { value: '신통', text: '申通(÷7500)' }, { value: '공식택배', text: '官方快递(÷8000)' }];
const CARRIER_DIVISOR = { 신통: 7500, 공식택배: 8000 };
// 「长*宽*高」 식 → 체적중량(kg, 소수 2자리). 숫자·*·x·×·공백만 허용(eval 금지). 못 읽으면 ''
function volumeWeight(expr, carrier) {
  const div = CARRIER_DIVISOR[carrier];
  if (!div || !/^[0-9.xX×*\s]+$/.test(expr || '')) return '';
  const p = String(expr).split(/[*xX×\s]+/).filter(Boolean).map(Number);
  if (p.length !== 3 || !p.every(n => Number.isFinite(n) && n > 0)) return '';
  return String(Math.round(p[0] * p[1] * p[2] / div * 100) / 100);
}
const RETURN_ENUM_OPTIONS = { 택배사구분: CARRIERS, 운송장입력주체: WAYBILL_OWNERS, 운송장발급방식: WAYBILL_MODES, 지급내역: PAY_DETAILS };
// 처리방식 탭(处理方式 기준). 그 밖의 처리방식은 全部 에서만 본다
const METHOD_TABS = [
  { value: '', text: '全部' },
  { value: '반품', text: '退货' },
  { value: '환불', text: '退款' },
  { value: '교환', text: '换货' },
  { value: '판매자재발송', text: '补发' }
];
// 처리방식(상세 칸을 고르는 기준) — 처리결과를 고르면 그것, 아니면 입고처리 화면에서 정한 처리방식
const METHOD_TEXT = { 판매자재발송: '商家补发', 반품: '退货', 교환: '换货' };
const RESOLVE_METHOD_TEXT = { 입고완료처리: '入库完成处理', 반품: '退货', 폐기: '废弃', 입고대기: '入库待发', 판매자재발송: '商家补发', 교환: '换货' };
const METHOD_FROM_STATUS = { 판매자재발송: '판매자재발송', 반품진행: '반품', 교환진행: '교환' };
const EDIT_KEYS = ['처리진행', '새답글', '처리결과', '처리결과메모', '재발송운송장번호', '가격'];
// 태그는 배열이라 따로 비교한다(isDirty·saveRow)
const TAG_KEYS = ['처리방식태그', '처리진도태그', '办理진도태그'];
const SIMPLE_KEYS = ['창고진도']; // 단일 값(EDIT_KEYS 와 같은 방식으로 비교·저장)
const sameTags = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);
// 仓储问题统计(2026-10-18 SYL #76) — 퇴화정보 칸의 하나로 저장(서버 IMPORT_ERROR_RETURN_ENUMS)
const STORAGE_ISSUES = [{ value: '', text: '-' }, { value: '待办发布问题', text: '待办发布问题' }, { value: '不良/少货数量统计错误', text: '不良/少货数量统计错误' }, { value: '验货问题', text: '验货问题' }, { value: '公司损失', text: '公司损失' }, { value: '补发验收超时', text: '补发验收超时' }, { value: '未核对退货明细', text: '未核对退货明细' }, { value: '订单多次发待办', text: '订单多次发待办' }, { value: '到货未点数', text: '到货未点数' }, { value: '未核对到货箱数', text: '未核对到货箱数' }, { value: '不良/少货数量虚报', text: '不良/少货数量虚报' }, { value: '少货未称重', text: '少货未称重' }, { value: '签收超3日未找到货', text: '签收超3日未找到货' }, { value: '待办订单号与选项不符', text: '待办订单号与选项不符' }, { value: '一个选项多次处理售后', text: '一个选项多次处理售后' }, { value: '仓库丢件', text: '仓库丢件' }, { value: '作业包装损坏造成损失', text: '作业包装损坏造成损失' }, { value: '整单退货，待办发错数量', text: '整单退货，待办发错数量' }, { value: '未统计不良/少货数量', text: '未统计不良/少货数量' }, { value: '退货问题', text: '退货问题' }, { value: '超时未回复确认', text: '超时未回复确认' }, { value: '未清点到货包装数量', text: '未清点到货包装数量' }, { value: '订单逾期验货', text: '订单逾期验货' }, { value: '多来货未告知', text: '多来货未告知' }, { value: '待办信息不全', text: '待办信息不全' }, { value: '数量对不上', text: '数量对不上' }, { value: '面单/称重照片不全或传错', text: '面单/称重照片不全或传错' }];
const RETURN_KEYS = ['주소', '운송장번호', '중량', '대형운송장번호', '환불금액', '운임금액', '운임부담', '운임입금일', '운송장입력주체', '운송장발급방식', '수기운송장정보', '지급내역', '규격식', '택배사구분', '체적중량', '창고중량표기', '반품옵션수량', '창고문제통계'];

export default {
  name: 'LogisImportErrorListV0',
  data() {
    const today = new Date();
    const from = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      pageTitle: coupassMenuTitle('logis.logisimporterrorlist'),
      canDelete: true,
      errorTypes: ERROR_TYPES,
      progressOptions: PROGRESS,
      methodTagOptions: tagOptions(METHOD_TAGS),
      warehouseOptions: [{ value: '', text: '仓储进度(选择)' }].concat(WAREHOUSE_PROGRESS.map(t => ({ value: t, text: t }))),
      handleTagOptions: tagOptions(HANDLE_TAGS),
      confirmTabs: CONFIRM_TABS,
      confirmTab: '',
      progressTagOptions: tagOptions(PROGRESS_TAGS),
      progressFilterOptions: [{ value: '__open', text: '未完成' }, { value: '', text: '全部' }].concat(PROGRESS),
      resultOptions: RESULTS,
      methodTabs: METHOD_TABS,
      methodTab: '',
      freightPayerOptions: FREIGHT_PAYERS_NEW,
      carrierOptions: CARRIERS,
      waybillOwnerOptions: WAYBILL_OWNERS,
      waybillModeOptions: WAYBILL_MODES,
      payDetailOptions: PAY_DETAILS,
      storageIssueOptions: STORAGE_ISSUES,
      startdate: this.ymd(from),
      enddate: this.ymd(today),
      companyid: '',
      errorType: '',
      mentionList: [],   // 내 계정이 @된 미확인 회신
      showMentions: false,
      mentionBusy: false,
      staffList: [],   // @멘션 후보(쿠패스 직원 ↔ 딩톡 이름) — 팝업을 열 때 한 번 받는다
      progressFilter: '__open',   // 기본 = 未完成(처리완료 제외). 새로고침하면 다시 기본값
      keyword: '',
      creatorFilter: '',
      resultFilter: '',
      errTypeAll: ERROR_TYPES_MAIN.concat(ERROR_TYPES_LEGACY),   // 라벨 색 순서(入库错误登记 목록 순서)
      prodOpen: {},   // 商品名 칸 펼침(기본 접힘)
      picked: [],             // 체크한 대표 건 _id (일괄 완료·삭제)
      batching: false,
      showPurge: false,       // 已删除 목록 «彻底删除» 확인창(#167)
      colFilter: { date: '', cust: '', order: '', etype: '', prod: '', opinion: '', progress: '', creator: '' },   // 제목줄 필터 (2026-10-18 SYL #85)
      statusFilter: 'open',   // 代办状态: open=未完成(기본, 完成代办 없는 건) / done=已完成 / ''=全部 (2026-10-18 SYL #74)
      showPhotos: false,
      photoRow: null,
      photoIdx: 0,
      photoFallback: false,
      zipping: false,
      companyOptions: [],
      rows: [],
      loading: false,
      exporting: false,
      showDelete: false,
      deleteTarget: null,
      showEdit: false,
      editTarget: null,
      editForm: { 오류유형: '', 오류내용: '', 오류수량: '', 출고가능수량: '', 미구성양품수량: '', 작업내용: '', 이미지: [] },
      editGroup: [],
      editForms: {},   // 옵션(건)별 수정 중인 값 — 저장 한 번에 전부(2026-10-19 SYL #88)
      editUploading: false,
      editing: false,
      openedFocus: '',
      showDaiban: false, daibanRow: null, // 代办明细 팝업(#49)
      showDetail: false,
      detailRow: null,
      deleting: false
    };
  },
  watch: {
    '$route.query.focus'() {
      this.openFocused();
    }
  },
  computed: {
    editTypeOptions() {
      // 새 13종 + 이 건에 예전 유형이 저장돼 있으면 그 값 하나(없애면 빈 칸이 된다)
      const cur = this.editTarget && this.editTarget._상세 ? this.editTarget._상세.오류유형 : '';
      const old = ERROR_TYPES_LEGACY.find(x => x.value === cur);
      return [{ value: '', text: '请选择', disabled: true }].concat(ERROR_TYPES_MAIN, old ? [old] : []);
    },
    // 필터·정렬을 거친 모든 건(합치기 전) — 엑셀 내보내기는 이걸 쓴다
    flatRows() {
      const kw = this.keyword.toLowerCase();
      const hit = r => [r._주문번호, r.화주주문번호, r.companyid].some(v => v && String(v).toLowerCase().includes(kw));
      const list = this.rows.filter(r => (!kw || hit(r)) &&
        // 未完成 기본값은 처리완료 제외. 검색어가 있으면 완료 건도 포함해서 찾는다
        (kw || this.statusMatch(r)) &&
        this.colMatch(r) &&
        (!this.methodTab || this.savedMethodOf(r) === this.methodTab) &&
        this.inConfirmTab(r, this.confirmTab) &&
        (!this.resultFilter || (this.resultFilter === '__none' ? !r._saved.처리결과 : r._saved.처리결과 === this.resultFilter)) &&
        (!this.creatorFilter || (this.creatorFilter === '__none' ? !r._등록자 : r._등록자 === this.creatorFilter)));
      // 같은 订单号(+화주) 건은 붙여서 보여 준다 — 첫 등장 순서는 그대로(2026-10-16 SYL #42). 주문번호가 없으면 묶지 않는다
      const order = [];
      const byKey = new Map();
      list.forEach(r => {
        const k = r._주문번호 ? r.companyid + '|' + r._주문번호 : '__' + r._id;
        if (!byKey.has(k)) { byKey.set(k, []); order.push(k); }
        byKey.get(k).push(r);
      });
      return [].concat(...order.map(k => byKey.get(k)));
    },
    // 같은 화주 + 订单号 + 바코드(같은 상품)의 오류 건은 1행으로 합쳐 보인다(2026-10-16 SYL #52) — 처리할 때 중복 선택되지 않게.
    // 대표 = 그 묶음의 첫 건. 나머지(members)는 대표 칸 안에 사유로 같이 나오고, 처리방식·진도 선택은 모두에게 같이 저장된다
    merged() {
      const rows = [];
      const members = {};
      const pos = new Map();
      this.flatRows.forEach(r => {
        if (!r._주문번호) { rows.push(r); return; }
        const k = r.companyid + '|' + r._주문번호;
        if (!pos.has(k)) { pos.set(k, r._id); members[r._id] = []; rows.push(r); } else { members[pos.get(k)].push(r); }
      });
      return { rows, members };
    },
    viewRows() {
      return this.merged.rows;
    },
    allPicked() {
      const sel = this.viewRows.filter(r => !r.삭제됨 || this.statusFilter === 'deleted');
      return sel.length > 0 && sel.every(r => this.picked.includes(r._id));
    },
    memberMap() {
      return this.merged.members;
    },
    // 客户处理意见 에서 고른 값으로 입력칸을 정한다 — 태그가 있으면 태그(서버와 같은 규칙), 없으면 옛 값
    drResult() {
      const r = this.daibanRow;
      if (!r) return '';
      const tags = r._edit.처리방식태그 || [];
      return tags.length ? deriveResult(tags) : (r._edit.처리결과 || '');
    },
    // 처리방식을 여러 개 고르면 고른 것 전부의 입력칸을 합쳐 보인다(2026-10-18 SYL #69) — 우선순위 하나만 쓰면 补发+少不良退款 에서 한쪽 칸이 사라진다
    drResultSet() {
      const r = this.daibanRow;
      if (!r) return new Set();
      const tags = r._edit.처리방식태그 || [];
      if (!tags.length) return new Set([r._edit.처리결과 || '']);
      return new Set(tags.map(t => METHOD_TO_RESULT[t]).filter(Boolean));
    },
    drNeedsShip() {
      return ['판매자재발송', '교환'].some(x => this.drResultSet.has(x));
    },
    drNeedsReturn() {
      return ['반품', '교환', '환불'].some(x => this.drResultSet.has(x));
    },
    // 代办明细 팝업에서 쓰는 행(템플릿의 dr)
    dr() {
      return this.daibanRow;
    },
    // 같은 订单号 묶음 정보 — { 행 _id: { size, n, first, last, grp(번호) } }
    orderGroup() {
      const out = {};
      let grp = 0;
      let i = 0;
      const rows = this.viewRows;
      while (i < rows.length) {
        const key = rows[i]._주문번호 ? rows[i].companyid + '|' + rows[i]._주문번호 : null;
        let j = i + 1;
        while (key && j < rows.length && rows[j]._주문번호 && rows[j].companyid + '|' + rows[j]._주문번호 === key) j++;
        const size = j - i;
        if (size > 1) grp++;
        for (let k = i; k < j; k++) out[rows[k]._id] = { size, n: k - i + 1, first: k === i, last: k === j - 1, grp };
        i = j;
      }
      return out;
    },
    // 처리방식(处理方式) 필터 — 처리결과 전체 값 + 미정
    resultFilterOptions() {
      return [{ value: '', text: '全部' }].concat(RESULTS.filter(x => x.value), [{ value: '__none', text: '未定' }]);
    },
    // 현재상태(当前状态) 필터 — 목록에 나온 processStatus 만
    // 代办状态(2026-10-18 SYL #74) — 办公室处理进度 에 «完成代办» 가 있으면 已完成, 그 밖은 전부 未完成
    statusFilterOptions() {
      return [{ value: '', text: '全部' }, { value: 'open', text: '未完成' }, { value: 'done', text: '已完成' }, { value: 'deleted', text: '<已删除>' }];
    },
    // 创建人 = 오류를 등록한 직원(처리이력 첫 기록의 처리자). 목록에 나온 이름만 고른다(등록자를 알 수 없는 옛 건은 「-」)
    creatorOptions() {
      const vals = [...new Set(this.rows.map(r => r._등록자).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      return [{ value: '', text: '全部' }].concat(vals.map(value => ({ value, text: value })), [{ value: '__none', text: '- (无记录)' }]);
    }
  },
  mounted() {
    this.search();
    // 다른 사람이 바꾼 태그·새 代办 반영(2026-10-20 SYL #62) — 주기 재조회 + 탭 복귀 시 재조회. 편집 중이면 건너뛴다
    this._liveAt = Date.now();
    this.loadMentions();
    this._mentionTimer = setInterval(() => { if (!document.hidden) this.loadMentions(); }, 10000);
    this._liveTimer = setInterval(() => { if (!document.hidden) this.refreshQuiet(); }, 2000);   // 2초마다(SYL 요청 — 정식 실시간 푸시 전 임시) — 다른 계정의 수정·회신이 화면을 그대로 둬도 반영(SYL 10-20)
    this._onVisible = () => { if (!document.hidden && Date.now() - this._liveAt > 10000) this.refreshQuiet(); };
    document.addEventListener('visibilitychange', this._onVisible);
  },
  beforeDestroy() {
    clearInterval(this._liveTimer);
    clearInterval(this._mentionTimer);
    document.removeEventListener('visibilitychange', this._onVisible);
  },
  methods: {
    ymd(v) {
      if (!v) return '';
      const d = new Date(typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v) : v);
      if (isNaN(d.getTime())) return String(v).slice(0, 10);
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    },
    ymdhm(v) {
      const d = new Date(Number(v));
      if (!v || isNaN(d.getTime())) return '';
      return this.ymd(d) + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
    },
    // 답글 목록 — 옛 처리내용(한 줄짜리)은 작성자·시각 없는 첫 답글로 보여 주고 그 뒤에 새 답글을 이어 붙인다. 최신이 위
    repliesOf(r) {
      const list = Array.isArray(r._상세.답글) ? r._상세.답글 : [];
      const old = r._상세.처리내용 ? [{ 내용: r._상세.처리내용, 작성자: '', 시각: 0 }] : [];
      return old.concat(list).reverse();
    },
    teamText(r) {
      const t = r.등록조;
      if (!t) return '-';
      return t.분류 || '未分类';
    },
    // 목록에서 드롭다운을 닫을 때 — 바뀐 게 있으면 바로 저장(저장 중이면 건너뜀)
    // 退货/补发 칸 등을 쓰다 칸을 벗어나면 바로 저장 — 보이는 保存 버튼이 없던 때 값이 날아간 회귀(2026-10-19 SYL #96)
    onLeftFocusout(e) {
      const r = this.daibanRow;
      if (!r || r.삭제됨) return;
      const next = e.relatedTarget;
      if (next && e.currentTarget.contains(next) && next.tagName !== 'BUTTON') return;   // 칸 안에서 옮겨 다니는 중
      setTimeout(() => this.saveIfDirty(r), 0);
    },
    saveIfDirty(r) {
      if (r && this.isDirty(r) && !r._saving) this.saveRowGroup(r);
    },
    // 알림(仓储待确认)을 눌러 들어오면 그 건의 代办明细 팝업을 연다 — 합쳐진 건이면 그 대표 건으로(2026-10-16 SYL #54)
    openFocused() {
      const f = this.$route && this.$route.query && this.$route.query.focus;
      if (!f || this.openedFocus === f) return;
      const hit = this.rows.find(r => r._id === f);
      if (!hit) return;
      const head = this.viewRows.find(v => v._id === f || (this.memberMap[v._id] || []).some(m => m._id === f)) || hit;
      this.openedFocus = f;
      this.openDaiban(head);
    },
    async loadMentions() {
      try {
        const { data } = await axios.post('/api/daiban/mentions/pending');
        if (data.요청결과 === '성공') this.mentionList = data.list || [];
      } catch (e) {
        console.error('@待确认 목록 불러오기 실패:', e);
      }
    },
    async confirmMention(m) {
      if (this.mentionBusy) return;
      this.mentionBusy = true;
      try {
        const { data } = await axios.post('/api/daiban/mentions/confirm', m ? { id: m.id } : { all: true });
        if (data.요청결과 !== '성공') alert('确认失败: ' + ((data.error && data.error.errorMessage) || ''));
        await this.loadMentions();
      } catch (e) {
        console.error('@待确认 확인 실패:', e);
        alert('确认失败(네트워크 오류)');
      } finally {
        this.mentionBusy = false;
      }
    },
    // 항목을 누르면 그 代办 팝업으로 — 지금 목록(기간·필터)에 없으면 안내한다
    openMention(m) {
      const hit = this.rows.find(r => r._id === m.reqId);
      if (!hit) { alert('当前列表里没有这条代办，请调整登记日期或筛选条件后再点(현재 목록에 없는 건 — 기간·필터를 넓혀 주세요)'); return; }
      const head = this.viewRows.find(v => v._id === m.reqId || (this.memberMap[v._id] || []).some(x => x._id === m.reqId)) || hit;
      this.showMentions = false;
      this.openDaiban(head);
    },
    openDaiban(r) {
      this.loadStaff();
      this.daibanRow = r;
      this.showDaiban = true;
    },
    isVideoUrl(u) {
      return /\.(mp4|mov|m4v|webm|avi|mkv)(\?|$)/i.test(String(u || ''));
    },
    openPhotos(r, i) {
      this.photoRow = r;
      this.photoIdx = i || 0;
      this.photoFallback = false;
      this.showPhotos = true;
    },
    // 사진 전체를 zip 1개로 — 압축파일 이름 = 품명(금지 문자는 _, 80자), 안은 <주문번호>/<순번>.확장자 (주문번호별 폴더)
    async downloadPhotos(r) {
      const imgs = r._이미지;
      if (!imgs.length || this.zipping) return;
      this.zipping = true;
      this.photoFallback = false;
      try {
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        const order = r._주문번호 || 'photo';
        let ok = 0;
        for (let i = 0; i < imgs.length; i++) {
          try {
            const res = await fetch(imgs[i]);
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const blob = await res.blob();
            const m = /\.(jpe?g|png|gif|webp|bmp)(?:\?|$)/i.exec(imgs[i]);
            const ext = m ? m[1].toLowerCase() : ((blob.type || '').split('/')[1] || 'jpg').replace('jpeg', 'jpg');
            zip.file(order + '/' + String(i + 1).padStart(2, '0') + '.' + ext, blob);
            ok++;
          } catch (e) {
            console.error('입고오류 사진 받기 오류:', imgs[i], e);
          }
        }
        if (!ok) {
          this.photoFallback = true;
          alert('图片无法一次性下载，请在窗口下方逐张打开后另存');
          return;
        }
        const name = String(r.상품명 || '').replace(/[\\/:*?"<>]/g, '_').trim().slice(0, 80) || order;
        const url = URL.createObjectURL(await zip.generateAsync({ type: 'blob' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = name + '.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        if (ok < imgs.length) {
          this.photoFallback = true;
          alert('有 ' + (imgs.length - ok) + ' 张图片未能下载，请在窗口下方逐张打开');
        }
      } catch (e) {
        console.error('입고오류 사진 zip 오류:', e);
        this.photoFallback = true;
        alert('下载失败，请在窗口下方逐张打开图片');
      } finally {
        this.zipping = false;
      }
    },
    // 확인내용 订单号 복사 · 물류추적(운송장번호로 快递100 조회 페이지를 새 창에 연다 — 택배사는 자동 인식)
    // 原因 글(입력한 그대로 — 값이 비어 있는 항목 제목 줄도 숨기지 않는다: SYL 정정 2026-10-18)
    reasonText(m) {
      return String((m && m._상세 && m._상세.오류내용) || '').trim();
    },
    // 같은 订单号·상품의 옵션(건)마다 올린 사진 — 옵션별로 묶고, 큰 그림 창은 전부를 한 줄로 이어 넘긴다
    confirmImagesRow(r) {
      const all = [];
      this.confirmItems(r).forEach(m => { (m._이미지 || []).forEach(u => all.push(u)); });
      return Object.assign({}, r, { _이미지: all });
    },
    // 옵션(건) m 의 사진 — 큰 그림 창의 전체 순번(idx)을 그대로 쓴다
    imageItemsOf(r, m) {
      let idx = 0;
      let out = [];
      this.confirmItems(r).some(x => {
        const items = (x._이미지 || []).map(url => ({ url, idx: idx++ }));
        if (x === m) { out = items; return true; }
        return false;
      });
      return out;
    },
    confirmImageGroups(r) {
      let idx = 0;
      const out = [];
      this.confirmItems(r).forEach(m => {
        const items = (m._이미지 || []).map(url => ({ url, idx: idx++ }));
        if (items.length) out.push({ label: m._옵션 || m.바코드 || '-', items });
      });
      return out;
    },
    alertNoEdit() {
      alert('只有登记人本人或所在小组组长可以修改(수정은 등록한 본인 또는 같은 小组 조장만 할 수 있습니다). 需要权限请联系管理员(권한이 필요하면 관리자에게 문의)');
    },
    alertNoDelete() {
      alert('仅代办组可以删除(삭제는 代办组 권한이 있는 계정만 할 수 있습니다). 需要权限请联系管理员(권한이 필요하면 관리자에게 문의)');
    },
    confirmItems(r) {
      return [r].concat((this.memberMap && this.memberMap[r._id]) || []);
    },
    // 화면 표시용 — 같은 원본(부분입고원본id)에서 쪼개져 나온 같은 옵션의 오류 줄은 첫 줄 하나로 합쳐 보인다(저장·처리는 줄마다 그대로, 2026-10-25 SYL #148)
    foldKeyOf(m) {
      return (m.부분입고원본id || m._id) + '|' + (m._옵션 || m.바코드 || '');
    },
    displayItems(r) {
      const seen = new Set();
      return this.confirmItems(r).filter(m => {
        const k = this.foldKeyOf(m);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    },
    foldedOf(r, m) {
      const k = this.foldKeyOf(m);
      return this.confirmItems(r).filter(x => this.foldKeyOf(x) === k);
    },
    foldQty(r, m) {
      const list = this.foldedOf(r, m);
      if (list.length < 2) return m._오류수량 === '' ? '-' : m._오류수량;
      const nums = list.map(x => Number(x._오류수량)).filter(n => Number.isFinite(n) && n > 0);
      return nums.length ? nums.reduce((a, b) => a + b, 0) : '-';
    },
    imageItemsFolded(r, m) {
      return [].concat(...this.foldedOf(r, m).map(x => this.imageItemsOf(r, x)));
    },
    // 处理记录 = 확인내용에 보이는 옵션 전부의 이력(2026-10-22 SYL #128). 대표 건만 보이면 나중에 추가된 옵션의 기록이 빠진다.
    // 옵션이 여럿이면 줄 앞에 그 옵션(중문속성)을 붙이고, 줄 맨 앞 시각(YYYY-MM-DD HH:mm)으로 시간순
    histLines(r) {
      const items = this.confirmItems(r);
      if (items.length < 2) return (r._이력 || '').split('\n');
      // 기록 한 건은 여러 줄일 수 있다(오류내용 줄바꿈) — 시각으로 시작하는 줄이 새 기록, 나머지는 앞 기록에 붙인다
      const entries = [];
      items.forEach(m => {
        let cur = null;
        String(m._이력 || '').split('\n').filter(Boolean).forEach(l => {
          if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2} /.test(l)) {
            cur = { time: l.slice(0, 16), body: l.slice(17), opt: m._옵션 };
            entries.push(cur);
          } else if (cur) {
            cur.body += '\n' + l;
          } else {
            cur = { time: '', body: l, opt: m._옵션 };
            entries.push(cur);
          }
        });
      });
      entries.sort((a, b) => a.time.localeCompare(b.time));
      // 같은 시각(분)에 처리·제출된 기록은 한 덩어리로 — 옵션 표기는 각 기록 앞에 그대로 둔다(2026-10-25 SYL)
      const lines = [];
      for (let i = 0; i < entries.length;) {
        let j = i;
        while (j < entries.length && entries[j].time === entries[i].time) j++;
        const parts = entries.slice(i, j).map(e => (e.opt ? '[' + e.opt + '] ' : '') + e.body);
        lines.push((entries[i].time ? entries[i].time + ' ' : '') + parts.join('\n    '));
        i = j;
      }
      return lines;
    },
    // 确认内容 전체 복사: 订单号 / (옵션마다) 중문속성·下单数量·原因, 옵션 사이는 빈 줄
    copyConfirm(r) {
      const blocks = this.confirmItems(r).map(m => [m._옵션 || '', '下单数量：' + (m._구매수량.total || '-'), '原因：' + (this.reasonText(m) || '-')].filter(x => x !== '').join('\n'));
      this.copyText([r._주문번호 || ''].concat(blocks.length ? [blocks.join('\n\n')] : []).filter(x => x !== '').join('\n'));
    },
    // 订单号 클릭 → 1688 «已买到的货品» 에서 그 订单号로 조회(2026-10-19 SYL #100). 订单号는 같이 복사해 둔다(검색칸에 붙여넣기용)
    open1688Order(no) {
      const n = String(no || '').trim();
      if (!n) return;
      this.copyText(n);
      window.open('https://trade.1688.com/order/buyer_order_list.htm?orderId=' + encodeURIComponent(n) + '&tradeId=' + encodeURIComponent(n), '_blank', 'noopener');
    },
    copyText(no) {
      const done = () => this.$bvToast.toast('已复制', { title: '复制', variant: 'success', solid: true, autoHideDelay: 1200, toaster: 'b-toaster-bottom-right' });
      const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = String(no);
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); done(); } catch (e) { console.error('订单号 복사 실패:', e); }
        ta.remove();
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(String(no)).then(done).catch(e => { console.error('订单号 복사 실패:', e); fallback(); });
      else fallback();
    },
    // 운송장번호로 조회 — 百度 검색은 택배 조회 카드가 뜨고(kind 'baidu'), 안 나오면 快递100(kind 'k100')
    trackParcel(no, kind) {
      const n = String(no || '').trim();
      if (!n) return;
      const url = kind === 'k100' ? 'https://www.kuaidi100.com/chaxun?nu=' + encodeURIComponent(n) : 'https://www.baidu.com/s?wd=' + encodeURIComponent(n);
      window.open(url, '_blank', 'noopener');
    },
    // 运费到账时间: 저장은 YYYY-MM-DD, 화면은 «MM.DD» (2026-10-18 SYL #72)
    freightDateText(r) {
      if (r._fdTxt !== undefined) return r._fdTxt;
      const v = (r._edit.반품 && r._edit.반품.운임입금일) || '';
      const m = v.match(/^\d{4}-(\d{2})-(\d{2})$/);
      return m ? m[1] + '.' + m[2] : v;
    },
    onFreightDateInput(r, e) {
      const raw = String(e.target.value || '').trim();
      this.$set(r, '_fdTxt', raw);
      let mo; let d; let y = new Date().getFullYear();
      const old = (r._edit.반품.운임입금일 || '').match(/^(\d{4})-/);
      if (old) y = Number(old[1]);
      let m = raw.match(/^(\d{1,2})[.\-/](\d{1,2})$/);
      if (m) { mo = Number(m[1]); d = Number(m[2]); } else if ((m = raw.match(/^(\d{4})[.\-/]?(\d{2})[.\-/]?(\d{2})$/))) { y = Number(m[1]); mo = Number(m[2]); d = Number(m[3]); }
      if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) r._edit.반품.운임입금일 = y + '-' + String(mo).padStart(2, '0') + '-' + String(d).padStart(2, '0');
    },
    onFreightDateBlur(r) {
      this.$set(r, '_fdTxt', undefined);
    },
    fmtDel(v) {
      const d = new Date(v);
      return isNaN(d.getTime()) ? '' : d.toLocaleString('zh-CN', { hour12: false });
    },
    colMatch(r) {
      const f = this.colFilter;
      const has = (txt, k) => !k || String(txt || '').toLowerCase().includes(k.toLowerCase());
      return has(r._등록일, f.date) &&
        has([r.companyid, this.teamText(r)].join(' '), f.cust) &&
        has([r._주문번호, r.화주주문번호].join(' '), f.order) &&
        has(this.errorTypeText(r._상세 && r._상세.오류유형), f.etype) &&
        has([r.상품명, r.바코드, r._옵션].join(' '), f.prod) &&
        has(this.tagSummary(r._edit.처리방식태그, r._edit.처리결과, 'method'), f.opinion) &&
        has(this.tagSummary(r._edit.처리진도태그, r._edit.처리진행, 'progress'), f.progress) &&
        has(r._등록자, f.creator);
    },
    clearColFilter() {
      Object.keys(this.colFilter).forEach(k => { this.colFilter[k] = ''; });
    },
    toggleAllPicked(v) {
      this.picked = v ? this.viewRows.filter(r => !r.삭제됨 || this.statusFilter === 'deleted').map(r => r._id) : [];
    },
    pickedRows() {
      return this.viewRows.filter(r => this.picked.includes(r._id));
    },
    // 체크한 건 일괄 «完成代办» — 办公室处理进度에 태그를 넣고 건마다 저장
    // «完成代办» 표시 켜기/끄기 — 합쳐진 건(같은 订单号·상품)이면 모두에게 같이
    async setComplete(r, done) {
      for (const x of this.confirmItems(r)) {
        const { data } = await axios.post('/api/reqpurchase/import/error/handle', { _id: x._id, 완료처리: done });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '失败');
        this.$set(x._상세, '완료', done ? { 완료일: Date.now(), 완료자: '' } : null);
      }
    },
    // 삭제한 代办 복구 — 삭제 기록(<已删除>)에서만(2026-10-19 SYL #100)
    async restoreDeleted(r) {
      if (!confirm('恢复这条已删除的代办? (삭제한 代办를 복구합니다)')) return;
      try {
        // 옵션이 여러 개였던 건은 옵션마다 삭제 기록이 따로 있다 — 전부 복구한다(대표 한 건만 복구하면 옵션이 1개만 남는다, #135)
        const fails = [];
        for (const x of this.confirmItems(r)) {
          const { data } = await axios.post('/api/reqpurchase/import/error/restore', { _id: x._id });
          if (data.요청결과 !== '성공') fails.push((data.error && data.error.errorMessage) || '');
        }
        if (fails.length) { alert('恢复失败: ' + fails.join(' / ')); return; }
        this.rows = this.rows.filter(x => x !== r);
        this.showDaiban = false;
        this.$bvToast.toast('已恢复', { title: '恢复', variant: 'success', solid: true, autoHideDelay: 3000, toaster: 'b-toaster-bottom-right' });
      } catch (e) {
        console.error('代办 복구 오류:', e);
        alert('恢复失败: ' + e.message);
      }
    },
    async toggleComplete(r) {
      const done = !this.isDaibanDone(r);
      if (!confirm(done ? '标记为「完成代办」? (완료 처리합니다)' : '取消「完成代办」? (완료를 취소합니다)')) return;
      try {
        await this.setComplete(r, done);
        this.$bvToast.toast(done ? '已完成' : '已取消完成', { title: '完成代办', variant: 'success', solid: true, autoHideDelay: 3000, toaster: 'b-toaster-bottom-right' });
      } catch (e) {
        console.error('完成代办 오류:', e);
        alert('操作失败: ' + e.message);
      }
    },
    async batchComplete() {
      const list = this.pickedRows().filter(r => !this.isDaibanDone(r));
      if (!list.length) { alert('所选项目已全部完成(선택한 건은 이미 모두 완료)'); return; }
      if (!confirm('将所选 ' + list.length + ' 件标记为「完成代办」? (선택한 ' + list.length + '건을 完成代办 처리합니다)')) return;
      this.batching = true;
      try {
        for (const r of list) await this.setComplete(r, true);
        this.picked = [];
        this.$bvToast.toast('已完成 ' + list.length + ' 件', { title: '批量完成', variant: 'success', solid: true, autoHideDelay: 1800, toaster: 'b-toaster-bottom-right' });
      } catch (e) {
        console.error('일괄 완료 오류:', e);
        alert('批量完成失败: ' + e.message);
      } finally {
        this.batching = false;
      }
    },
    async batchDelete() {
      const list = this.pickedRows();
      if (!list.length) return;
      if (!this.canDelete) { this.alertNoDelete(); return; }
      if (!confirm('删除所选 ' + list.length + ' 件? 删除后可在「代办状态 → <已删除>」中查看。(선택한 ' + list.length + '건을 삭제합니다)')) return;
      this.batching = true;
      let ok = 0;
      const fail = [];
      try {
        for (const r of list) {
          for (const x of [r].concat(this.memberMap[r._id] || [])) {
            const { data } = await axios.post('/api/reqpurchase/import/error/delete', { _id: x._id });
            if (data.요청결과 === '성공') ok++; else fail.push((x._주문번호 || x._id) + ': ' + ((data.error && data.error.errorMessage) || ''));
          }
        }
      } catch (e) {
        console.error('일괄 삭제 오류:', e);
        fail.push(e.message);
      } finally {
        this.batching = false;
        this.picked = [];
      }
      if (fail.length) alert('部分删除失败(일부 실패):\n' + fail.slice(0, 5).join('\n'));
      else this.$bvToast.toast('已删除 ' + ok + ' 件', { title: '批量删除', variant: 'success', solid: true, autoHideDelay: 1800, toaster: 'b-toaster-bottom-right' });
      await this.search();
    },
    // 已删除 목록에서 체크한 건 완전삭제 — 복구 불가. 확인창에서 한 번 더 확인한 뒤 서버(/import/error/purge)로(2026-10-26 SYL #167)
    async batchPurge() {
      const ids = [];
      for (const r of this.pickedRows()) for (const x of this.confirmItems(r)) ids.push(x._id);
      if (!ids.length) { this.showPurge = false; return; }
      if (!this.canDelete) { this.showPurge = false; this.alertNoDelete(); return; }
      this.batching = true;
      let ok = 0;
      const fail = [];
      try {
        for (let i = 0; i < ids.length; i += 200) {
          const { data } = await axios.post('/api/reqpurchase/import/error/purge', { _ids: ids.slice(i, i + 200) });
          if (data.요청결과 === '성공') ok += data.삭제수 || 0; else fail.push((data.error && data.error.errorMessage) || '失败');
        }
      } catch (e) {
        console.error('代办 완전삭제 오류:', e);
        fail.push(e.message);
      } finally {
        this.batching = false;
        this.showPurge = false;
        this.picked = [];
      }
      if (fail.length) alert('彻底删除失败(완전삭제 실패):\n' + fail.slice(0, 5).join('\n'));
      else this.$bvToast.toast('已彻底删除 ' + ok + ' 件', { title: '彻底删除', variant: 'success', solid: true, autoHideDelay: 1800, toaster: 'b-toaster-bottom-right' });
      await this.search();
    },
    isDaibanDone(r) {
      return !!(r._상세 && r._상세.완료);   // «完成代办» 버튼을 누른 건만(2026-10-18 SYL #85) — 办公室办理进度 태그와 별개
    },
    statusMatch(r) {
      if (this.statusFilter === 'deleted') return true;   // 삭제 건 조회 모드 — 서버가 삭제 기록만 내려 준다
      if (this.statusFilter === 'done') return this.isDaibanDone(r);
      if (this.statusFilter === 'open') return !this.isDaibanDone(r);
      return true;
    },
    errorTypeValuesOf(r) {
      return [...new Set([r].concat((this.memberMap && this.memberMap[r._id]) || []).map(m => m._상세.오류유형).filter(Boolean))];
    },
    // 같은 订单号 묶음의 错误类型 — 같으면 한 줄, 다르면 줄마다
    errorTypesOf(r) {
      return [...new Set([r].concat((this.memberMap && this.memberMap[r._id]) || []).map(m => this.errorTypeText(m._상세.오류유형)).filter(Boolean))];
    },
    // 드롭다운 선택값의 색(닫힌 상태에서도 유지) — 목록 순서(빈 값 제외)대로 팔레트를 돌려 쓴다(2026-10-18 SYL #75)
    optStyle(value, opts) {
      const pal = [["#1b7f3b","#dff3e4"],["#a21caf","#fae8ff"],["#c2410c","#ffedd5"],["#1d4ed8","#dbeafe"],["#0f766e","#ccfbf1"],["#be185d","#fce7f3"],["#a16207","#fef3c7"],["#4338ca","#e0e7ff"],["#b91c1c","#fee2e2"],["#334155","#e2e8f0"]];
      const i = (opts || []).filter(o => o.value !== '' && o.value != null).findIndex(o => o.value === value);
      if (i < 0) return {};
      const [c, b] = pal[i % pal.length];
      return { color: c, background: b, fontWeight: 700, fontFamily: '"Microsoft YaHei", "微软雅黑", sans-serif' };
    },
    // 다중 선택 드롭다운(客户处理意见 등) 버튼 색 — 첫 번째로 고른 항목의 순서 번호. 없으면 색 없음
    tagColorClass(selected, optionValues) {
      const first = (selected || []).map(v => optionValues.indexOf(v)).filter(i => i >= 0).sort((a, b) => a - b)[0];
      return first === undefined ? '' : 'tagc-' + (first % 10);
    },
    // 错误类型 선택 색 — 목록 순서(첫 «선택» 제외)대로 팔레트를 돌려 쓴다
    errTypeStyle(value, opts) {
      const pal = [["#1b7f3b","#dff3e4"],["#a21caf","#fae8ff"],["#c2410c","#ffedd5"],["#1d4ed8","#dbeafe"],["#0f766e","#ccfbf1"],["#be185d","#fce7f3"],["#a16207","#fef3c7"],["#4338ca","#e0e7ff"],["#b91c1c","#fee2e2"],["#334155","#e2e8f0"]];
      const i = (opts || []).filter(o => o.value).findIndex(o => o.value === value);
      if (i < 0) return {};
      const [c, b] = pal[i % pal.length];
      return { color: c, background: b, fontWeight: 700, fontFamily: '"Microsoft YaHei", "微软雅黑", sans-serif' };
    },
    errorTypeText(v) {
      const t = ERROR_TYPES.find(x => x.value && x.value === v);
      return t ? t.text : (v || '');
    },
    errorStatusText(v) {
      return errorStatusText(v);
    },
    statusText(v) {
      return processStatusText(v);
    },
    // 탭 기준 처리방식 — 저장된 값(편집 중인 값으로 하면 고르는 순간 행이 탭에서 사라진다)
    savedMethodOf(r) {
      if (METHOD_TEXT[r._saved.처리결과] || r._saved.처리결과 === '환불') return r._saved.처리결과;
      return METHOD_FROM_STATUS[r._상세.오류상태] || '';
    },
    // 같은 화주·订单号·상품은 1행으로 합쳐 보이므로(#52) 탭 숫자도 합친 줄 수로 센다(2026-10-16 SYL #54)
    countMerged(list) {
      const keys = new Set();
      let n = 0;
      list.forEach(r => {
        if (!r._주문번호) { n++; return; }
        const k = r.companyid + '|' + r._주문번호;
        if (!keys.has(k)) { keys.add(k); n++; }
      });
      return n;
    },
    tabCount(v) {
      return this.countMerged(v ? this.rows.filter(r => this.savedMethodOf(r) === v) : this.rows);
    },
    // 같은 订单号 묶음 — 묶음은 왼쪽 색 띠(번호 홀짝으로 두 색), 첫 줄 위에 굵은 선
    groupClass(r) {
      const g = this.orderGroup[r._id];
      if (!g || g.size < 2) return '';
      return ['import-error-list__grp', g.grp % 2 ? 'import-error-list__grp--a' : 'import-error-list__grp--b', g.first ? 'import-error-list__grp--first' : ''];
    },
    // 드롭다운 버튼 글자 — 고른 개수, 없으면 «请选择»(옛 건은 옛 값을 아래 작은 글자로 보여 준다)
    // 代办确认 탭 — 저장된 처리진도 태그 중 하나라도 그 탭의 목록에 있으면 보인다(태그가 없는 옛 건은 탭에 안 나온다)
    inConfirmTab(r, tab) {
      if (!tab) return true;
      const t = CONFIRM_TABS.find(x => x.value === tab);
      const saved = (r._saved && r._saved.처리진도태그) || [];
      return !!t && saved.some(x => t.tags.includes(x));
    },
    confirmTabCount(tab) {
      return this.countMerged(this.rows.filter(r => this.inConfirmTab(r, tab)));
    },
    // 드롭다운 버튼 글자 — 고른 개수 대신 고른 태그 글자
    tagSummary(tags, legacy, kind) {
      if (tags && tags.length) return tags.join(' / ');
      return kind === 'progress' ? '办公室处理进度(选择)' : '客户处理意见(选择)';
    },
    progressText(v) {
      const t = PROGRESS.find(x => x.value === (v || '대기'));
      return t ? t.text : v;
    },
    resultText(v) {
      const t = RESULTS.find(x => x.value && x.value === v);
      return t ? t.text : (v || '');
    },
    recalcVolume(b) {
      b.체적중량 = volumeWeight(b.규격식, b.택배사구분);
    },
    // 运费承担方式 목록 — 새 11종. 이미 예전 값이 저장된 건은 그 값만 하나 더 보여 준다(없애면 빈 칸이 된다)
    freightOptsFor(cur) {
      const old = FREIGHT_PAYERS_OLD.find(x => x.value === cur);
      return old ? FREIGHT_PAYERS_NEW.concat([old]) : FREIGHT_PAYERS_NEW;
    },
    freightWithReturn(v) {
      return FREIGHT_WITH_RETURN.includes(v);
    },
    freightPayerText(v) {
      const t = FREIGHT_PAYERS.find(x => x.value && x.value === v);
      return t ? t.text : (v || '');
    },
    // 처리이력 — 저장값은 한국어 키라 중국어로 바꿔 보인다(이 화면은 중국어 전용)
    historyStateText(v) {
      if (v === '처리기록') return '处理记录';
      if (v === '등록수정') return '登记修改';
      return errorStatusText(v);
    },
    historyMemo(h, 상세) {
      // 등록(접수) 메모 = 「오류유형 - 오류내용」 → 유형만 중국어로 바꾸고 담당자가 쓴 내용은 그대로
      if (h.상태 === '접수') {
        const q = h.수량 !== undefined && h.수량 !== null && h.수량 !== '' ? h.수량 : 상세.오류수량;
        const head = this.errorTypeText(상세.오류유형) + (q !== undefined && q !== null && q !== '' ? ' · 数量' + q : '');
        return [head, 상세.오류내용].filter(Boolean).join(' - ');
      }
      // 처리방식 확정(resolve) 메모가 처리방법 그대로면 중국어로
      // 让步接收 → 客户废弃 개명 전에 남은 기록도 새 이름으로 보인다
      // 오류유형 수정 기록(서버가 저장값 그대로 «错误类型 修改: A → B» 로 남김)도 중국어 라벨로 — 한글 값이 그대로 보이던 것(2026-10-25 SYL)
      return RESOLVE_METHOD_TEXT[h.메모] || String(h.메모 || '').replace(/让步接收/g, '客户废弃')
        .replace(/(错误类型 修改: )(.+?) → (.+?)(?= \/ |\n|$)/g, (m, p, a, b) => p + this.errorTypeText(a) + ' → ' + this.errorTypeText(b));
    },
    methodText(m) {
      return m === '환불' ? '退款' : (METHOD_TEXT[m] || '');
    },
    methodOf(r) {
      if (METHOD_TEXT[r._edit.처리결과]) return r._edit.처리결과;
      // 退款 — 상세 창(退款金额 칸)을 다시 열 수 있게 링크만 보인다. 탭 분류(savedMethodOf)에는 넣지 않는다
      if (r._edit.처리결과 === '환불') return '환불';
      return METHOD_FROM_STATUS[r._상세.오류상태] || '';
    },
    errorStatusVariant(status) {
      const map = { '접수': 'danger', '입고완료처리': 'success', '반품진행': 'warning', '폐기진행': 'dark', '입고대기': 'secondary', '판매자재발송': 'info', '교환진행': 'info' };
      return map[status] || 'secondary';
    },
    editFrom(상세) {
      const rt = 상세.반품정보 || {};
      const 반품 = {};
      RETURN_KEYS.forEach(k => { 반품[k] = rt[k] == null ? '' : String(rt[k]); });
      // 退货重量(仓库) — 長宽高·重量·体积重量을 한 칸으로 합쳤다. 새 칸이 비어 있고 옛 값이 있으면 «70*60*50 21.1kg» 꼴로 보여 준다(저장은 바꿨을 때만)
      if (!반품.창고중량표기) {
        const legacy = [반품.규격식, 반품.중량 ? 반품.중량 + 'kg' : ''].filter(Boolean).join(' ');
        if (legacy) 반품.창고중량표기 = legacy;
      }
      return {
        처리진행: 상세.처리진행 || '대기',
        새답글: '',
        처리결과: 상세.처리결과 || '',
        처리결과메모: 상세.처리결과메모 || '',
        재발송운송장번호: 상세.재발송운송장번호 || '',
        가격: 상세.가격 === '' || 상세.가격 == null ? '' : String(상세.가격),
        처리방식태그: Array.isArray(상세.처리방식태그) ? [...상세.처리방식태그] : [],
        처리진도태그: Array.isArray(상세.처리진도태그) ? [...상세.처리진도태그] : [],
        창고진도: 상세.창고진도 || '',
        办理진도태그: Array.isArray(상세.办理진도태그) ? [...상세.办理진도태그] : [],
        반품
      };
    },
    copyEdit(e) {
      return { ...e, 반품: { ...e.반품 }, 처리방식태그: [...e.처리방식태그], 처리진도태그: [...e.처리진도태그], 办理진도태그: [...e.办理진도태그] };
    },
    returnDiff(r) {
      return RETURN_KEYS.filter(k => String(r._edit.반품[k] == null ? '' : r._edit.반품[k]) !== r._saved.반품[k]);
    },
    isDirty(r) {
      return EDIT_KEYS.concat(SIMPLE_KEYS).some(k => r._edit[k] !== r._saved[k]) || TAG_KEYS.some(k => !sameTags(r._edit[k], r._saved[k])) || this.returnDiff(r).length > 0;
    },
    openDetail(r) {
      this.detailRow = r;
      this.showDetail = true;
    },
    async saveDetail() {
      if (!this.detailRow) return;
      await this.saveRow(this.detailRow);
      if (this.detailRow && !this.isDirty(this.detailRow)) this.showDetail = false;
    },
    toRow(r) {
      const 상세 = r.입고오류상세 || {};
      const 이력 = Array.isArray(상세.처리이력) ? 상세.처리이력 : [];
      const 원수량 = r.부분입고원수량 != null ? r.부분입고원수량 : (r.원구매요청수량 != null ? r.원구매요청수량 : r.구매요청수량);
      const edit = this.editFrom(상세);
      return {
        ...r,
        _상세: 상세,
        _replyFiles: [],   // 回复에 붙일 첨부 URL(저장하면 비운다)
        _upl: false,
        _옵션: [r.옵션1_중국어, r.옵션2_중국어].filter(Boolean).join(' '),
        _이미지: Array.isArray(상세.이미지) ? 상세.이미지.filter(Boolean) : [],
        _등록일: this.ymdhm(이력[0] && 이력[0].처리일),
        _이력: 이력.map(h => [this.ymdhm(h.처리일), this.historyStateText(h.상태), h.처리자, this.historyMemo(h, 상세)].filter(Boolean).join(' ')).join('\n'),
        _주문번호: (r.주문정보 && r.주문정보['1688주문번호']) || '',
        // 创建人 = 오류를 등록한 직원 — 처리이력 첫 기록의 처리자(2026-10-16 SYL #49)
        _등록자: (이력[0] && 이력[0].처리자) || '',
        // 1688 주문 낱개 수량이 있으면 그것, 없으면 처음 구매요청 수량
        _총주문수량: (r.주문정보 && r.주문정보.주문수량) || 원수량 || '',
        // 入库处理 의 «采购数量» 와 같은 표기: 총수량(구매요청수량 x 판매구성수량 P) — 2026-10-16 SYL #39
        _구매수량: (() => {
          const base = parseInt(원수량) || 0;
          const p = !r.판매구성수량 || r.판매구성수량 === '단품' ? 1 : (parseInt(r.판매구성수량) || 1);
          return { base, p, total: base * p };
        })(),
        // 등록 때 넣은 오류 수량. 원래 건에서 떼어 낸 오류 건이면 그 건의 수량이 곧 오류 수량
        _오류수량: (상세.오류수량 !== '' && 상세.오류수량 != null) ? 상세.오류수량 : (r.부분입고원본id ? r.구매요청수량 : ''),
        _sellerOpenId: (r.item && r.item.sellerOpenId) || '',
        _edit: edit,
        _saved: this.copyEdit(edit),
        _open: false,
        _openHist: false,
        _saving: false
      };
    },
    // 편집·팝업·드롭다운이 열려 있거나 저장 안 된 입력이 있으면 true — 이때는 자동 재조회로 덮어쓰지 않는다
    liveBlocked() {
      if (document.hidden || this.loading || this.batching || this.exporting || this.editUploading || this.editing || this.deleting) return true;
      if (this.showDetail || this.showEdit || this.showDelete || this.showPhotos) return true;
      // 저장 중·업로드 중인 행이 있으면 쉰다. 입력 중이거나 저장 안 한 값은 막지 않고, 새로 받을 때 칸별로 보존한다(mergeLive) — 안 그러면 포커스만 있어도 영원히 갱신이 안 된다(2026-10-20 SYL #62)
      return this.rows.some(r => r._saving || r._upl);
    },
    // 새로 받은 행에 옛 행의 «내가 아직 저장 안 한 칸» 만 얹는다 — 나머지는 다른 계정이 저장한 최신 값으로 바뀐다
    mergeLive(row, old) {
      EDIT_KEYS.concat(SIMPLE_KEYS).forEach(k => { if (old._edit[k] !== old._saved[k]) row._edit[k] = old._edit[k]; });
      TAG_KEYS.forEach(k => { if (!sameTags(old._edit[k], old._saved[k])) row._edit[k] = [...old._edit[k]]; });
      RETURN_KEYS.forEach(k => {
        const mine = old._edit.반품[k], base = old._saved.반품[k];
        if (String(mine == null ? '' : mine) !== base) row._edit.반품[k] = mine;
      });
      row._replyFiles = old._replyFiles || [];
      // 화면 상태(탭·@후보·운임입금일 입력 중 글·이력 펼침)도 옛 행에서 이어 받는다 — 안 그러면 2초마다 탭이 처리내용으로 튕기고 @ 후보가 사라진다
      ['_tab', '_mq', '_mpos', '_mentions', '_fdTxt', '_histOpen'].forEach(k => { if (old[k] !== undefined) this.$set(row, k, old[k]); });
      return row;
    },
    async refreshQuiet() {
      if (this._quietBusy || this.liveBlocked()) return;
      this._quietBusy = true;
      try {
        await this.search({ quiet: true });
      } finally {
        this._quietBusy = false;
      }
    },
    async search(opt) {
      const quiet = !!(opt && opt.quiet);
      if (!quiet) this.loading = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/errorlist', {
          companyid: this.companyid, 오류유형: this.errorType, start: this.startdate, end: this.enddate, 처리진행: this.progressFilter === '__open' ? '' : this.progressFilter,
          삭제조회: this.statusFilter === 'deleted'
        });
        if (quiet && this.liveBlocked()) return;   // 기다리는 사이 편집이 시작됐으면 버린다
        if (data.요청결과 !== '성공') {
          if (quiet) return;
          alert('搜索失败: ' + ((data.error && data.error.errorMessage) || ''));
          this.rows = [];
          return;
        }
        this.canDelete = data.삭제가능 !== false;   // 서버가 false 를 줄 때만 숨김 (구버전 응답 호환)
        if (data.truncated && !quiet) alert('结果过多，仅显示前3000条，请缩小日期范围');
        const prev = quiet ? new Map(this.rows.map(r => [r._id, r])) : null;
        this.picked = quiet ? this.picked.filter(id => (data.rows || []).some(x => x._id === id)) : [];
        this.rows = (data.rows || []).map(r => {
          const row = this.toRow(r);
          const old = prev && prev.get(row._id);
          if (old) { row._open = old._open; row._openHist = old._openHist; this.mergeLive(row, old); }   // 펼침 상태 유지
          return row;
        });
        const set = new Set(this.companyOptions);
        this.rows.forEach(r => { if (r.companyid) set.add(r.companyid); });
        // 열려 있는 代办明细 는 새로 받은 같은 행으로 다시 연결한다(안 하면 옛 행을 계속 보여 준다)
        if (this.daibanRow) { const cur = this.rows.find(x => x._id === this.daibanRow._id); if (cur) this.daibanRow = cur; }
        this.openFocused();
        this.companyOptions = [...set].sort();
      } catch (e) {
        console.error('입고오류 리스트 검색 오류:', e);
        if (!quiet) alert('搜索错误: ' + e.message);
      } finally {
        if (!quiet) this.loading = false;
        else this._liveAt = Date.now();
      }
    },
    // 바꾼 칸만 보낸다 — 처리일·처리자는 서버가 찍는다
    // 합쳐진 건(#52)이 있으면 고른 처리방식·진도 값을 모두에게 같이 저장한다 — 대표 건 저장 뒤 나머지 건에 같은 값을 넣고 저장
    // 代办明细 回复 @멘션·첨부(2026-10-18 SYL #70) — @ 를 치면 직원 목록이 뜨고, 고른 직원에게 딩톡 알림이 간다. 사진·영상은 S3 에 올려 주소만 저장한다
    async loadStaff() {
      if (this.staffList.length) return;
      try {
        const { data } = await axios.post('/api/daiban/staffList');
        if (data.요청결과 === '성공') this.staffList = data.staff || [];
      } catch (e) {
        console.error('직원 목록 불러오기 실패:', e);
      }
    },
    mentionHits(r) {
      const q = r && r._mq;
      if (q === null || q === undefined) return [];
      const k = String(q).toLowerCase();
      return this.staffList.filter(x => !k || x.name.toLowerCase().includes(k) || x.login.toLowerCase().includes(k)).slice(0, 8);
    },
    onReplyInput(r, e) {
      const el = e.target;
      const pos = el.selectionStart;
      const m = el.value.slice(0, pos).match(/@([^\s@]*)$/);
      this.$set(r, '_mq', m ? m[1] : null);
      this.$set(r, '_mpos', pos);
    },
    pickMention(r, x) {
      const t = r._edit.새답글 || '';
      const pos = r._mpos == null ? t.length : r._mpos;
      r._edit.새답글 = t.slice(0, pos).replace(/@([^\s@]*)$/, '@' + x.name + ' ') + t.slice(pos);
      const have = r._mentions || [];
      if (!have.includes(x.login)) this.$set(r, '_mentions', have.concat(x.login));
      this.$set(r, '_mq', null);
    },
    onReplyEnter(r) {
      const hits = this.mentionHits(r);
      if (hits.length) { this.pickMention(r, hits[0]); return; }
      this.saveRowGroup(r);
    },
    // 回复 첨부 올리기 — 사진·영상 여러 장
    async onReplyFiles(r, e) {
      const files = Array.from((e.target && e.target.files) || []);
      e.target.value = '';
      if (!files.length) return;
      this.$set(r, '_upl', true);
      try {
        for (const f of files) {
          if (r._replyFiles.length >= 10) { alert('最多10个(10개까지)'); break; }
          r._replyFiles.push(await uploadImportPhoto(f, 'rep-' + r._id));
        }
      } catch (err) {
        console.error('회신 첨부 업로드 실패:', err);
        alert('上传失败(업로드 실패): ' + err.message);
      } finally {
        this.$set(r, '_upl', false);
      }
    },
    // 回复 삭제 — 쓴 본인 또는 代办组(서버가 판정). 처리记录에도 남는다
    async deleteReply(r, a) {
      if (!confirm('删除这条回复? (이 회신을 삭제합니다)')) return;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/error/handle', { _id: r._id, 답글삭제: a.시각 });
        if (data.요청결과 !== '성공') { alert('删除失败: ' + ((data.error && data.error.errorMessage) || '')); return; }
        this.$set(r._상세, '답글', (r._상세.답글 || []).filter(x => x.시각 !== a.시각));
        r._이력 = (r._이력 ? r._이력 + '\n' : '') + [this.ymdhm(Date.now()), '处理记录', '', '删除回复 ' + String(a.내용 || '').slice(0, 300)].join(' ');
      } catch (e) {
        console.error('回复 삭제 오류:', e);
        alert('删除失败: ' + e.message);
      }
    },
    async saveRowGroup(r) {
      const members = (this.memberMap && this.memberMap[r._id]) || [];
      await this.saveRow(r);
      for (const m of members) {
        m._edit.처리방식태그 = [...r._edit.처리방식태그];
        m._edit.처리진도태그 = [...r._edit.처리진도태그];
        m._edit.办理진도태그 = [...r._edit.办理진도태그];
        m._edit.창고진도 = r._edit.창고진도;
        if (this.isDirty(m)) await this.saveRow(m);
      }
    },
    async saveRow(r) {
      const body = { _id: r._id };
      EDIT_KEYS.forEach(k => { if (r._edit[k] !== r._saved[k]) body[k] = r._edit[k]; });
      if (r._edit.창고진도 !== r._saved.창고진도) body['仓储进度'] = r._edit.창고진도;
      if (!sameTags(r._edit.办理진도태그, r._saved.办理진도태그)) body['办理进度태그'] = [...r._edit.办理진도태그];
      ['처리방식태그', '처리진도태그'].forEach(k => { if (!sameTags(r._edit[k], r._saved[k])) body[k] = [...r._edit[k]]; });
      // 새 답글은 덮어쓰지 않고 서버가 한 건 추가한다
      if ('새답글' in body || (r._replyFiles && r._replyFiles.length)) {
        const txt = String(r._edit.새답글 || '').trim();
        if (txt || (r._replyFiles && r._replyFiles.length)) {
          body.답글추가 = txt || '(附件)';
          if (r._replyFiles.length) body.답글첨부 = r._replyFiles.slice();
          // 글에 아직 «@이름» 이 남아 있는 사람만 알린다(지운 사람은 뺀다)
          const ids = (r._mentions || []).filter(l => { const n = (this.staffList.find(x => x.login === l) || {}).name; return n && body.답글추가.includes('@' + n); });
          if (ids.length) body.답글멘션 = ids;
        }
        delete body.새답글;
      }
      const rd = this.returnDiff(r);
      if (rd.length) {
        body.반품정보 = {};
        rd.forEach(k => { body.반품정보[k] = String(r._edit.반품[k] == null ? '' : r._edit.반품[k]); });
      }
      r._saving = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/import/error/handle', body);
        if (data.요청결과 !== '성공') {
          alert('保存失败: ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        EDIT_KEYS.forEach(k => { if (k in body) this.$set(r._상세, k, k === '가격' && body[k] !== '' ? Number(body[k]) : body[k]); });
        TAG_KEYS.forEach(k => { if (k in body) this.$set(r._상세, k, [...body[k]]); });
        if ('仓储进度' in body) this.$set(r._상세, '창고진도', body['仓储进度']);
        if ('办理进度태그' in body) this.$set(r._상세, '办理진도태그', [...body['办理进度태그']]);
        // 서버가 태그에서 맞춘 옛 값(처리결과·처리진행)을 화면에도 같이 반영 — 상단 탭·필터·상세칸이 이 값을 읽는다
        if ('처리방식태그' in body && !('처리결과' in body)) { r._edit.처리결과 = r._edit.처리방식태그.length ? deriveResult(r._edit.처리방식태그) : (r._saved.처리방식태그.length ? '' : r._edit.처리결과); this.$set(r._상세, '처리결과', r._edit.처리결과); }
        if ('처리진도태그' in body && !('처리진행' in body)) { r._edit.처리진행 = r._edit.처리진도태그.length ? deriveProgress(r._edit.처리진도태그) : (r._saved.처리진도태그.length ? '대기' : r._edit.처리진행); this.$set(r._상세, '처리진행', r._edit.처리진행); }
        if (body.반품정보) this.$set(r._상세, '반품정보', { ...(r._상세.반품정보 || {}), ...body.반품정보 });
        if (data.답글) this.$set(r._상세, '답글', (Array.isArray(r._상세.답글) ? r._상세.답글 : []).concat(data.답글));
        // 저장한 내용을 처리记录에도 바로 보여 준다(서버가 남긴 메모 그대로) — 새로고침 없이(2026-10-19 SYL #87)
        if (data.메모) r._이력 = (r._이력 ? r._이력 + '\n' : '') + [this.ymdhm(data.처리일), '处理记录', data.처리자, data.메모].filter(Boolean).join(' ');
        r._edit.새답글 = '';
        this.$set(r, '_mentions', []); this.loadMentions();
        this.$set(r, '_mq', null);
        r._replyFiles = [];
        this.$set(r._상세, '처리일', data.처리일);
        this.$set(r._상세, '처리자', data.처리자);
        r._edit.반품 = { ...r._edit.반품 };
        RETURN_KEYS.forEach(k => { r._edit.반품[k] = String(r._edit.반품[k] == null ? '' : r._edit.반품[k]); });
        r._saved = this.copyEdit(r._edit);
        this.$bvToast.toast('已保存', { title: '保存', variant: 'success', solid: true, autoHideDelay: 3000, toaster: 'b-toaster-bottom-right' });
      } catch (e) {
        console.error('입고오류 처리기록 저장 오류:', e);
        alert('保存失败: ' + e.message);
      } finally {
        r._saving = false;
      }
    },
    // 修改 = 入库错误登记와 같은 전체 화면. 같은 订单号·상품의 다른 옵션 건(#52)은 같은 창에서 눌러 바꿔 가며 고친다(2026-10-18 SYL #73)
    openEdit(r) {
      this.editGroup = this.confirmItems(r);
      this.editForms = {};
      this.loadEdit(r);
      this.showEdit = true;
    },
    loadEdit(r) {
      const d = r._상세 || {};
      this.editTarget = r;
      if (this.editForms[r._id]) { this.editForm = this.editForms[r._id]; return; }
      this.editForm = {
        오류유형: d.오류유형 || '',
        오류내용: d.오류내용 || '',
        오류수량: d.오류수량 === '' || d.오류수량 == null ? '' : d.오류수량,
        출고가능수량: d.출고가능수량 === '' || d.출고가능수량 == null ? '' : d.출고가능수량,
        미구성양품수량: d.미구성양품수량 == null ? '' : String(d.미구성양품수량),
        작업내용: d.작업내용 || '',
        이미지: Array.isArray(d.이미지) ? d.이미지.filter(Boolean).slice() : []
      };
      this.$set(this.editForms, r._id, this.editForm);
    },
    switchEdit(g) {
      if (this.editing) return;
      this.loadEdit(g);
    },
    async onEditFiles(e) {
      const files = Array.from((e.target && e.target.files) || []);
      e.target.value = '';
      if (!files.length || !this.editTarget) return;
      this.editUploading = true;
      try {
        for (const f of files) {
          if (this.editForm.이미지.length >= 30) { alert('最多30个(30개까지)'); break; }
          this.editForm.이미지.push(await uploadImportPhoto(f, 'err-' + this.editTarget._id));
        }
      } catch (err) {
        console.error('입고오류 수정 이미지 업로드 실패:', err);
        alert('上传失败(업로드 실패): ' + err.message);
      } finally {
        this.editUploading = false;
      }
    },
    // 바뀐 칸만 보낸다 — 권한(등록자 본인·같은 그룹 조장)은 서버가 판정한다
    editBody(r, f) {
      const d = r._상세 || {};
      const body = { _id: r._id };
      if (f.오류유형 !== (d.오류유형 || '')) body.오류유형 = f.오류유형;
      if (f.오류내용 !== (d.오류내용 || '')) body.오류내용 = f.오류내용;
      const 이전수량 = d.오류수량 === '' || d.오류수량 == null ? '' : d.오류수량;
      if (!r.부분입고원본id && f.오류수량 !== 이전수량) body.오류수량 = f.오류수량;
      const 이전가능 = d.출고가능수량 === '' || d.출고가능수량 == null ? '' : d.출고가능수량;
      if ((f.출고가능수량 === null ? '' : f.출고가능수량) !== 이전가능) body.출고가능수량 = f.출고가능수량 === null ? '' : f.출고가능수량;
      if (String(f.미구성양품수량 || '').trim() !== String(d.미구성양품수량 == null ? '' : d.미구성양품수량)) body.미구성양품수량 = String(f.미구성양품수량 || '').trim();
      if (String(f.작업내용 || '').trim() !== String(d.작업내용 || '')) body.작업내용 = String(f.작업내용 || '').trim();
      const 이전이미지 = Array.isArray(d.이미지) ? d.이미지.filter(Boolean) : [];
      if (JSON.stringify(f.이미지) !== JSON.stringify(이전이미지)) body.이미지목록 = f.이미지.slice();
      return body;
    },
    editDirtyOf(g) {
      const f = this.editForms[g._id];
      return !!f && Object.keys(this.editBody(g, f)).length > 1;
    },
    // 공용 保存 — 손댄 옵션 전부를 차례로 저장한다. 실패한 건에서 멈추고 창을 유지한다
    async confirmEdit() {
      // 열어 보지 않은 옵션은 입력칸이 아직 없다 — 예전에는 «지금 열린 옵션의 입력칸»으로 대신 비교해 그 값이 다른 옵션에도 저장됐다(2026-10-25 SYL). 입력칸이 있는 옵션만 저장한다
      const list = (this.editGroup.length ? this.editGroup : [this.editTarget]).filter(Boolean);
      const jobs = list.filter(g => this.editForms[g._id]).map(g => ({ r: g, body: this.editBody(g, this.editForms[g._id]) })).filter(j => Object.keys(j.body).length > 1);
      if (!jobs.length) { this.showEdit = false; return; }
      this.editing = true;
      try {
        for (const { r, body } of jobs) {
          const { data } = await axios.post('/api/reqpurchase/import/error/edit', body);
          if (data.요청결과 !== '성공') {
            alert('保存失败(' + (r._옵션 || r.바코드 || '') + '): ' + ((data.error && data.error.errorMessage) || ''));
            return;
          }
          ['오류유형', '오류내용', '출고가능수량', '미구성양품수량', '작업내용'].forEach(k => { if (k in body) this.$set(r._상세, k, body[k]); });
          if ('오류수량' in body) { this.$set(r._상세, '오류수량', body.오류수량); r._오류수량 = body.오류수량; }
          if ('이미지목록' in body) { this.$set(r._상세, '이미지', body.이미지목록.slice()); r._이미지 = body.이미지목록.slice(); }
        }
        this.showEdit = false;
        this.$bvToast.toast('已保存' + (jobs.length > 1 ? ' ' + jobs.length + '项' : ''), { title: '修改', variant: 'success', solid: true, autoHideDelay: 3000, toaster: 'b-toaster-bottom-right' });
      } catch (e) {
        console.error('입고오류 등록수정 오류:', e);
        alert('保存失败: ' + e.message);
      } finally {
        this.editing = false;
      }
    },
    askDelete(r) {
      this.deleteTarget = r;
      this.showDelete = true;
    },
    async confirmDelete() {
      const r = this.deleteTarget;
      if (!r) return;
      this.deleting = true;
      try {
        const targets = this.confirmItems(r);
        let data = {};
        const gone = new Set();
        for (const x of targets) {
          const res = await axios.post('/api/reqpurchase/import/error/delete', { _id: x._id });
          data = res.data;
          if (data.요청결과 !== '성공') {
            // 일부만 지워졌을 수 있다 — 지워진 줄은 목록에서 빼고 실패 사유를 알린다
            this.rows = this.rows.filter(y => !gone.has(y._id));
            alert('删除失败(' + (x._옵션 || x.바코드 || '') + '): ' + ((data.error && data.error.errorMessage) || ''));
            return;
          }
          gone.add(x._id);
        }
        this.rows = this.rows.filter(y => !gone.has(y._id));
        this.showDelete = false;
        this.$bvToast.toast('已删除' + (data.병합 ? '，已合并回原单' : '') + (data.status ? '，当前状态: ' + this.statusText(data.status) : ''), { title: '删除', variant: 'success', solid: true });
      } catch (e) {
        console.error('입고오류 삭제 오류:', e);
        alert('删除失败: ' + e.message);
      } finally {
        this.deleting = false;
      }
    },
    // 왕왕 — 구매요청 화면(openWangwangChat)과 같은 /api/alibaba/getWangwangUrl(판매자 openUid).
    // 응답을 기다린 뒤 여는 창은 팝업 차단에 걸리므로 빈 창을 먼저 연다
    async openWangwang(r) {
      const w = window.open('', '_blank');
      try {
        const url = await this.wangwangUrl(r._sellerOpenId);
        if (url && w) {
          w.location.href = url;
          return;
        }
        if (w) w.close();
        alert('获取旺旺链接失败');
      } catch (e) {
        console.error('왕왕 링크 오류:', e);
        if (w) w.close();
        alert('获取旺旺链接失败: ' + e.message);
      }
    },
    async wangwangUrl(openId) {
      if (!openId) return '';
      const { data } = await axios.post('/api/alibaba/getWangwangUrl', { toOpenUid: openId });
      if (data.요청결과 === '성공' && data.result) return data.result;
      console.error('왕왕 링크 실패:', data.error);
      return '';
    },
    async downloadExcel() {
      this.exporting = true;
      try {
        // 왕왕 링크는 저장값이 없어 판매자별로 한 번씩 받아 온다(같은 판매자는 한 번)
        const ww = {};
        // 같은 货主·订单号 건은 한 행 — 첫 행만 남기고 나머지 행의 옵션·오류유형·사진·회신은 합쳐 넣는다(2026-10-19 SYL #99)
        const gmap = new Map();
        this.flatRows.forEach(r => {
          const k = r._주문번호 ? r.companyid + '|' + r._주문번호 : '__' + r._id;
          if (!gmap.has(k)) gmap.set(k, []);
          gmap.get(k).push(r);
        });
        const groupOf = new Map();
        const list = [...gmap.values()].map(g => { groupOf.set(g[0], g); return g[0]; });
        const grp = r => groupOf.get(r) || [r];
        const imgsOf = r => [...new Set([].concat(...grp(r).map(x => x._이미지)))];
        const ids = [...new Set(list.map(r => r._sellerOpenId).filter(Boolean))].slice(0, 100);
        for (const id of ids) {
          try {
            ww[id] = await this.wangwangUrl(id);
          } catch (e) {
            console.error('엑셀 왕왕 링크 실패:', id, e);
          }
        }
        // 代办明细 한 칸 — 옵션마다 11개 항목을 줄바꿈으로, 옵션이 여럿이면 «选项1 / 选项2», 处理内容 는 주문당 한 번(2026-10-19 SYL #99)
        const detailText = r => {
          const items = [...new Map([].concat(...grp(r).map(x => this.confirmItems(x))).map(m => [m._id, m])).values()];
          const blocks = items.map((m, i) => [
            items.length > 1 ? '选项' + (i + 1) + ':' : '',
            '商品名:' + (m.상품명 || ''), '选项:' + (m._옵션 || ''), '条码:' + (m.바코드 || ''),
            '下单数量:' + (m._구매수량 && m._구매수량.total != null ? m._구매수량.total : ''), '错误数量:' + (m._오류수량 == null ? '' : m._오류수량),
            '错误类型:' + this.errorTypeText(m._상세.오류유형), '错误内容:' + (m._상세.오류내용 || ''),
            '可出库数量:' + (m._상세.출고가능수량 == null ? '' : m._상세.출고가능수량), '不成组良品数量:' + (m._상세.미구성양품수량 == null ? '' : m._상세.미구성양품수량),
            '是否已作业&作业内容:' + (m._상세.작업내용 || '')
          ].filter(x => x !== '').join('\n'));
          const reply = [...new Map([].concat(...grp(r).map(x => this.repliesOf(x))).map(a => [a.시각 + '|' + a.내용, a])).values()].sort((a, b) => (a.시각 || 0) - (b.시각 || 0)).map(a => [a.작성자, a.시각 ? this.ymdhm(a.시각) : '', a.내용].filter(Boolean).join(' ')).join('\n');
          return ['订单号:' + (r._주문번호 || '')].concat(blocks.join('\n\n'), '', '处理内容:' + reply).join('\n');
        };
        const headers = ['登记日期', '货主', '订单号', '商品链接', '旺旺链接', '创建人', '货主订单号', '错误类型', '客户处理意见', '办公室处理进度',
          '补发单号', '退货单号(小件)', '退货重量(kg)(仓库)', '大件退货单号', '退款金额(人民币)', '运费金额(人民币)', '运费承担方式', '运费到账时间',
          '退货地址', '仓储问题统计', '处理日', '当前状态', '处理记录', '代办明细', '代办状态', '图片'];
        const LINK_COL = headers.indexOf('商品链接');
        const WW_COL = headers.indexOf('旺旺链接');
        const IMG_COL = headers.length - 1;
        const maxImgs = Math.max(1, ...list.map(r => imgsOf(r).length));
        const head = headers.concat(Array.from({ length: maxImgs - 1 }, (_, i) => '图片' + (i + 2)));
        const rt = (r, k) => {
          const v = r._saved.반품[k];
          if (RETURN_ENUM_OPTIONS[k]) { const o = RETURN_ENUM_OPTIONS[k].find(x => x.value && x.value === v); return o ? o.text : v; }
          return k === '운임부담' ? this.freightPayerText(v) : v;
        };
        const tagsOrText = (tags, fallback) => (tags && tags.length ? tags.join(' / ') : fallback);
        const aoa = [head].concat(list.map(r => [
          r._등록일, r.companyid || '', r._주문번호, r.구매링크 ? 'link' : '', ww[r._sellerOpenId] ? 'link' : '', r._등록자 || '', r.화주주문번호 || '',
          [...new Set([].concat(...grp(r).map(x => this.errorTypeValuesOf(x))))].map(v => this.errorTypeText(v)).join(' / '),
          tagsOrText(r._saved.처리방식태그, this.resultText(r._saved.처리결과)),
          tagsOrText(r._saved.처리진도태그, this.progressText(r._saved.처리진행)),
          r._saved.재발송운송장번호, rt(r, '운송장번호'), rt(r, '창고중량표기'), rt(r, '대형운송장번호'), rt(r, '환불금액'), rt(r, '운임금액'), rt(r, '운임부담'), rt(r, '운임입금일'),
          rt(r, '주소'), rt(r, '창고문제통계'), this.ymdhm(r._상세.처리일),
          [this.statusText(r.processStatus), r._상세.오류상태 && r._상세.오류상태 !== '접수' ? this.errorStatusText(r._상세.오류상태) : ''].filter(Boolean).join(' / '), r._이력, detailText(r),
          r.삭제됨 ? '已删除' : (this.isDaibanDone(r) ? '已完成' : '未完成')
        ].concat(Array.from({ length: maxImgs }, (_, i) => imgsOf(r)[i] || ''))));
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        // 링크 칸은 화면처럼 「link」 글자 + 하이퍼링크, 사진은 주소 그대로 + 하이퍼링크(1행은 헤더)
        const link = (r, c, url) => {
          const cell = ws[XLSX.utils.encode_cell({ r, c })];
          if (url && cell) cell.l = { Target: url, Tooltip: url };
        };
        list.forEach((r, i) => {
          link(i + 1, LINK_COL, r.구매링크);
          link(i + 1, WW_COL, ww[r._sellerOpenId]);
          imgsOf(r).forEach((url, k) => link(i + 1, IMG_COL + k, url));
        });
        const wb = XLSX.utils.book_new();
        ws['!cols'] = head.map((h, i) => ({ wch: h === '代办明细' ? 60 : i < 4 ? 16 : 14 }));
        XLSX.utils.book_append_sheet(wb, ws, '代办列表');
        const d = new Date();
        const stamp = d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
        const tab = METHOD_TABS.find(t => t.value === this.methodTab);
        XLSX.writeFile(wb, '代办列表_' + (this.methodTab && tab ? tab.text + '_' : '') + stamp + '.xlsx');
      } catch (e) {
        console.error('입고오류 엑셀 오류:', e);
        alert('导出失败: ' + e.message);
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* 목록 칸 구분선·글자 크기(2026-10-16 SYL #49) */
.import-error-list__black, .import-error-list__black .text-muted { color: #000 !important; }
.import-error-list thead th { color: #000; }
.import-error-list { font-size: 0.95rem !important; }
.import-error-list th, .import-error-list td { border: 1px solid #adb5bd !important; }
.import-error-list th { font-size: 1rem; }
.import-error-list .small, .import-error-list small { font-size: 0.85rem !important; }
.import-error-list .badge { font-size: 0.85rem; padding: 4px 8px; }
/* 같은 订单号 묶음(2026-10-16 SYL #42) */
.import-error-list__grp > td:first-child { border-left-width: 5px; border-left-style: solid; }
.import-error-list__grp--a > td:first-child { border-left-color: #17a2b8; }
.import-error-list__grp--b > td:first-child { border-left-color: #fd7e14; }
.import-error-list__grp--first > td { border-top: 2px solid #adb5bd; }

// 열 너비는 colgroup 으로 고정하고 긴 칸은 말줄임(마우스를 올리면 전체). 操作 열은 오른쪽에 붙여 가로 스크롤해도 보이게(2026-10-03 YZY)
.import-error-list {
  max-height: 72vh;
  overflow: auto;
  font-size: 0.875rem;

  // 공통 스타일의 table overflow:hidden 이 sticky(제목 행·操作 열)를 막는다
  table {
    overflow: visible;
    table-layout: fixed;
    width: max-content;
    min-width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }

  th,
  td {
    padding: 3px 4px !important; // 테마의 .table td { padding: 15px 20px !important } 를 이긴다
    vertical-align: middle;
    line-height: 1.3;
  }

  th {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: sticky;
    top: 0;
    z-index: 2;
    background: #e9ecef;
  }

  &__op {
    position: sticky;
    right: 0;
    z-index: 1;
    background: #fff;
    box-shadow: -2px 0 4px rgba(0, 0, 0, .08);
  }

  th.import-error-list__op {
    z-index: 3;
    background: #e9ecef;
  }

  &__wrap {
    word-break: break-all;
  }

  &__minw {
    min-width: 0;
  }

  &__clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
    white-space: pre-line;

    &--3 {
      -webkit-line-clamp: 3;
    }

    &--all {
      display: block;
    }
  }

  &__pimg {
    width: 40px;
    height: 40px;
    object-fit: cover;
    flex: none;
    border-radius: 3px;
  }

  &__imgs {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;

    img {
      width: 26px;
      height: 26px;
      object-fit: cover;
      border-radius: 3px;
    }
  }

  // 테마가 입력칸을 40px 넘게 키운다 — 표 안에서는 낮게
  .custom-select-sm,
  .form-control-sm {
    font-size: 0.875rem;
    height: 28px !important;
    min-height: 0 !important;
    padding: 0 4px !important;
    line-height: 24px;
  }

  textarea.form-control-sm {
    height: 46px !important;
    resize: vertical;
    line-height: 1.3;
    padding: 2px 4px !important;
  }

  .btn-sm {
    padding: 1px 4px;
    font-size: 0.75rem;
  }
}
</style>

<!-- 处理方式·处理进度 드롭다운(2026-10-16 SYL) — 고른 태그를 검정 굵은 글씨로, 목록은 스크롤. 드롭다운 안쪽 요소라 scoped 가 아니다 -->
<style>
/* 错误类型 드롭다운 — 항목마다 색(글자+배경), 선택한 뒤 닫힌 상태에서도 그 색 유지, 微软雅黑 굵게 (2026-10-18 SYL) */
.err-type-select, .err-type-select option { font-family: "Microsoft YaHei", "微软雅黑", sans-serif !important; font-weight: 700; }
.err-type-select option:nth-child(10n+2) { color: #1b7f3b; background: #dff3e4; }
.err-type-select option:nth-child(10n+3) { color: #a21caf; background: #fae8ff; }
.err-type-select option:nth-child(10n+4) { color: #c2410c; background: #ffedd5; }
.err-type-select option:nth-child(10n+5) { color: #1d4ed8; background: #dbeafe; }
.err-type-select option:nth-child(10n+6) { color: #0f766e; background: #ccfbf1; }
.err-type-select option:nth-child(10n+7) { color: #be185d; background: #fce7f3; }
.err-type-select option:nth-child(10n+8) { color: #a16207; background: #fef3c7; }
.err-type-select option:nth-child(10n+9) { color: #4338ca; background: #e0e7ff; }
.err-type-select option:nth-child(10n+10) { color: #b91c1c; background: #fee2e2; }
.err-type-select option:nth-child(10n+11) { color: #334155; background: #e2e8f0; }
.import-error-list__tagbtn { color: #000 !important; font-weight: 700; white-space: normal; text-align: left; }
.import-error-list__tagmenu { max-height: 320px; overflow-y: auto; min-width: 260px; }
/* 代办明细 팝업(#58) */
.daiban-popup .import-error-list__tagbtn, .daiban-popup .import-error-list__tagbtn span { font-size: 15px !important; font-weight: 700; color: #000 !important; }
.daiban-popup .daiban-head { background: #f8f9fa; }
/* 左右 두 영역 따로 스크롤, 팝업 전체 스크롤은 없앤다(2026-10-26 SYL #155) */
@media (min-width: 992px) {
  .daiban-popup .modal-body { overflow: hidden; }
  .daiban-popup .daiban-split { height: calc(100vh - 150px); }
  .daiban-popup .daiban-pane { height: 100%; overflow-y: auto; }
}
/* 代办列表 — 검색줄 굵게·한 단계 크게 / 错误类型 라벨 / 商品名 접힘 (2026-10-18 SYL #80) */
.daiban-filterbar label, .daiban-filterbar .form-control, .daiban-filterbar .custom-select, .daiban-filterbar .btn, .daiban-filterbar > span { font-family: "Microsoft YaHei", "微软雅黑", sans-serif !important; font-weight: 400 !important; font-size: 12px !important; }
.daiban-colfilter th { padding: 2px !important; background: #f8f9fa; }
.daiban-colfilter .form-control { font-family: "Microsoft YaHei", "微软雅黑", sans-serif; font-size: 12px; font-weight: 400; padding: 1px 4px; height: 24px; }
.daiban-filterbar .form-control, .daiban-filterbar .custom-select { height: auto; }
.daiban-etag { display: inline-block; padding: 3px 8px; border-radius: 6px; font-size: 13px; line-height: 1.3; }
.daiban-prod-collapsed { height: 122px; overflow: hidden; }
.daiban-popup .opacity-60 { opacity: .6; }
.daiban-popup .daiban-right, .daiban-popup .daiban-right * { font-family: "Microsoft YaHei", "微软雅黑", sans-serif !important; }
.daiban-arrow { font-size: 44px; line-height: 1; padding: 6px 18px; font-weight: 700; border-width: 3px; }
/* 삭제한 건 — 팝업은 보기만 (입력·버튼 잠금) */
.daiban-readonly .modal-body input, .daiban-readonly .modal-body select, .daiban-readonly .modal-body textarea, .daiban-readonly .modal-body .btn, .daiban-readonly .modal-body .dropdown { pointer-events: none; opacity: .75; }
/* 客户处理意见 ~ 아래 왼쪽 칼럼: 微软雅黑·退货重量 입력칸 글자 크기(.875rem)로 통일 (2026-10-18 SYL #75) */
.daiban-popup .daiban-left, .daiban-popup .daiban-left * { font-family: "Microsoft YaHei", "微软雅黑", sans-serif !important; }
.daiban-popup .daiban-left, .daiban-popup .daiban-left label, .daiban-popup .daiban-left .small, .daiban-popup .daiban-left .form-control, .daiban-popup .daiban-left .custom-select,
.daiban-popup .daiban-left .form-control, .daiban-popup .daiban-left .custom-select, .daiban-popup .daiban-left .import-error-list__tagbtn { min-height: 36px; box-sizing: border-box; }
.daiban-popup .daiban-left .btn, .daiban-popup .daiban-left .import-error-list__tagbtn, .daiban-popup .daiban-left .import-error-list__tagbtn span, .daiban-popup .daiban-left .custom-control-label, .daiban-popup .daiban-left option { font-size: .875rem !important; }
/* 선택지 구분용 — 이 창의 모든 드롭다운 항목을 색을 돌려 가며 칠한다(네이티브 select 의 option · 체크박스 목록) */
.daiban-tabs .btn, .daiban-batch .btn, .daiban-batch span { font-family: "Microsoft YaHei", "微软雅黑", sans-serif; font-size: 14px; font-weight: 700; }
.daiban-tabs .btn small { font-size: 13px; font-weight: 700; }
.import-error-list td.import-error-list__wrap.small, .import-error-list td.import-error-list__wrap.small * { font-family: "Microsoft YaHei", "微软雅黑", sans-serif; }
.daiban-popup .modal-dialog { max-width: 1400px; }
.daiban-popup select option:nth-child(10n+2), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+1) { color: #1b7f3b; background: #dff3e4; font-weight: 700; }
.daiban-popup select option:nth-child(10n+3), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+2) { color: #a21caf; background: #fae8ff; font-weight: 700; }
.daiban-popup select option:nth-child(10n+4), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+3) { color: #c2410c; background: #ffedd5; font-weight: 700; }
.daiban-popup select option:nth-child(10n+5), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+4) { color: #1d4ed8; background: #dbeafe; font-weight: 700; }
.daiban-popup select option:nth-child(10n+6), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+5) { color: #0f766e; background: #ccfbf1; font-weight: 700; }
.daiban-popup select option:nth-child(10n+7), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+6) { color: #be185d; background: #fce7f3; font-weight: 700; }
.daiban-popup select option:nth-child(10n+8), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+7) { color: #a16207; background: #fef3c7; font-weight: 700; }
.daiban-popup select option:nth-child(10n+9), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+8) { color: #4338ca; background: #e0e7ff; font-weight: 700; }
.daiban-popup select option:nth-child(10n+10), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+9) { color: #b91c1c; background: #fee2e2; font-weight: 700; }
.daiban-popup select option:nth-child(10n+11), .daiban-popup .import-error-list__tagmenu .custom-control:nth-child(10n+10) { color: #334155; background: #e2e8f0; font-weight: 700; }
.import-error-list__tagbtn.tagc-0, .import-error-list__tagbtn.tagc-0 span { color: #1b7f3b !important; background: #dff3e4 !important; }
.import-error-list__tagbtn.tagc-1, .import-error-list__tagbtn.tagc-1 span { color: #a21caf !important; background: #fae8ff !important; }
.import-error-list__tagbtn.tagc-2, .import-error-list__tagbtn.tagc-2 span { color: #c2410c !important; background: #ffedd5 !important; }
.import-error-list__tagbtn.tagc-3, .import-error-list__tagbtn.tagc-3 span { color: #1d4ed8 !important; background: #dbeafe !important; }
.import-error-list__tagbtn.tagc-4, .import-error-list__tagbtn.tagc-4 span { color: #0f766e !important; background: #ccfbf1 !important; }
.import-error-list__tagbtn.tagc-5, .import-error-list__tagbtn.tagc-5 span { color: #be185d !important; background: #fce7f3 !important; }
.import-error-list__tagbtn.tagc-6, .import-error-list__tagbtn.tagc-6 span { color: #a16207 !important; background: #fef3c7 !important; }
.import-error-list__tagbtn.tagc-7, .import-error-list__tagbtn.tagc-7 span { color: #4338ca !important; background: #e0e7ff !important; }
.import-error-list__tagbtn.tagc-8, .import-error-list__tagbtn.tagc-8 span { color: #b91c1c !important; background: #fee2e2 !important; }
.import-error-list__tagbtn.tagc-9, .import-error-list__tagbtn.tagc-9 span { color: #334155 !important; background: #e2e8f0 !important; }
.daiban-popup .import-error-list__tagmenu .custom-control { padding-top: 2px; padding-bottom: 2px; }
</style>
